import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Mail, MessageCircle, Send, FileText, Sparkles, ArrowLeft,
  X, ShieldCheck, AlertCircle, Users, Search, Plus, Save, Wand2,
  Info, ChevronRight, ChevronDown, Paperclip,
} from 'lucide-react';
import { ComingWave } from './CiShared';
import RichEditor from './RichEditor';
import { guestDisplayName, initials, matchesSegment, SEGMENTS } from './ciUtils';
import { LINK_KINDS } from '../_shared/guestLinkTokens';

// A template counts as the celebration confirmation MMS when it carries the
// celebration image or link merge tags — so we can flag it in the SAME gold
// the guest profile uses, making the confirmation link unmistakable.
const isConfirmationTemplate = (t) => {
  if (!t) return false;
  const blob = `${t.name || ''} ${t.message_text || ''} ${(t.media_urls || []).join(' ')}`.toLowerCase();
  return blob.includes('celebration_mms_image_url')
    || blob.includes('celebration_site_url')
    || blob.includes('celebration confirmation');
};
import { sanitizeHtml } from '../../../utils/sanitizeHtml';
import {
  getGuests,
  getEmailTemplates,
  getSmsTemplates,
  getOutboundSettings,
  getEventConfig,
  getCelebrationConfig,
  getRsvpPendingAudience,
  previewOutboundEmail,
  previewOutboundSms,
  sendEmails,
  sendSms,
  createEmailTemplate,
  createSmsTemplate,
  askMiloAgent,
} from '../../../data/api';

// Create Communication — full-page builder, FULLY WIRED.
//
// Outbound safety: this screen NEVER bypasses the backend hold/test/live gate.
// Review & Schedule first calls previewOutbound* (which mints the stale-preview
// digest), then sendEmails/sendSms with that digest — exactly the SendPanel
// flow. The active send_mode (hold/test/live) is read from getOutboundSettings
// and surfaced before the user confirms, so nothing leaves the Hub unexpectedly.
//
// Channel → backend:
//   email      → sendEmails  (template-anchored, custom subject/body override)
//   invitation → sendEmails  (an invitation IS an email; same safe path)
//   sms        → sendSms
//   note       → internal only; saved as a draft, never sent outbound.

const TYPES = [
  { key: 'email', label: 'Email', Icon: Mail },
  { key: 'sms', label: 'SMS', Icon: MessageCircle },
  { key: 'invitation', label: 'Invite', Icon: Send },
  { key: 'note', label: 'Note', Icon: FileText },
];

// Audience segments + the matchesSegment classifier now live in the canonical
// ciUtils module (single source of truth, mirrored to the backend RSVP tokens).

// Real merge tokens — rendered/substituted by the backend send pipeline.
const MERGE_TAGS = [
  { token: '{{first_name}}', label: 'First name' },
  { token: '{{last_name}}', label: 'Last name' },
  { token: '{{guest_name}}', label: 'Full name' },
  { token: '{{event_name}}', label: 'Event name' },
  { token: '{{my_celebration_link}}', label: 'My Celebration link' },
];
const RSVP_TOKEN = '{{rsvp_link}}';
// Opt-in styled RSVP button. Inline styles (with a solid-color fallback before
// the gradient) so it survives in real email HTML. Inserted ONLY when the host
// clicks "RSVP button" — never auto-added — and fully editable/removable after.
// The {{rsvp_link}} href resolves per-guest at send time.
const RSVP_BUTTON_HTML = '<p style="text-align:center;margin:18px 0;"><a href="{{rsvp_link}}" style="display:inline-block;background:#C6A75A;background:linear-gradient(180deg,#E6C579,#C6A75A);color:#221b08;font-weight:700;font-size:14px;padding:12px 32px;border-radius:999px;text-decoration:none;font-family:inherit;">RSVP Now</a></p>';

function emailChannel(type) {
  return type === 'email' || type === 'invitation';
}

// Resolve merge tokens for the LIVE PREVIEW only, mirroring the backend send
// renderers (_render_sms_for_guest / _render_admin_email_for_guest) so the host
// sees what a guest actually receives instead of raw {{tokens}}. The real
// per-guest substitution still happens server-side at send time.
// Marker shown in the preview when a recipient has no RSVP token. NOT a URL —
// we never fake a link or generate a token (source of truth is parties.rsvp_token).
const RSVP_LINK_MISSING = '[RSVP link unavailable — no token]';
// Build the real RSVP link for a guest from their EXISTING party token only
// (party_rsvp_token mirror from /api/guests). Never generates a token.
function rsvpLinkForGuest(guest) {
  const token = String(guest?.party_rsvp_token || '').trim();
  return token ? `${window.location.origin}/invite/${token}` : '';
}
function resolveMergeTagsForPreview(text, guest, eventName, cel = null) {
  if (text == null || text === '') return text;
  const fn = String(guest?.first_name || '').trim();
  const ln = String(guest?.last_name || '').trim();
  const full = [fn, ln].filter(Boolean).join(' ').trim();
  const rsvp = rsvpLinkForGuest(guest) || RSVP_LINK_MISSING;
  const c = cel || {};
  return String(text)
    .replace(/\{\{\s*first_name\s*\}\}/gi, fn || 'there')
    .replace(/\{\{\s*last_name\s*\}\}/gi, ln || '')
    .replace(/\{\{\s*(guest_name|full_name)\s*\}\}/gi, full || fn || 'there')
    .replace(/\{\{\s*email\s*\}\}/gi, String(guest?.email || '').trim() || 'guest@example.com')
    .replace(/\{\{\s*event_name\s*\}\}/gi, String(eventName || '').trim() || 'your event')
    // Celebration confirmation tags (CEL-7). Resolved from the SAME sources the
    // backend substitutes at send time (party primary, party members' YES list,
    // event config, celebration config, party celebration token). Missing data
    // shows a clear bracket marker — never a fabricated value.
    .replace(/\{\{\s*head_first_name\s*\}\}/gi, c.headFirst || fn || 'there')
    .replace(/\{\{\s*attending_guest_names\s*\}\}/gi, c.attendingNames || '[no attending guests recorded]')
    .replace(/\{\{\s*event_date\s*\}\}/gi, c.eventDate || '[event date not set]')
    .replace(/\{\{\s*arrival_time\s*\}\}/gi, c.arrivalTime || '[arrival time not set]')
    .replace(/\{\{\s*venue_name\s*\}\}/gi, c.venueName || '[venue not set]')
    .replace(/\{\{\s*venue_address\s*\}\}/gi, c.venueAddress || '')
    .replace(/\{\{\s*celebration_site_url\s*\}\}/gi, c.celUrl || '[celebration link unavailable — no token]')
    .replace(/\{\{\s*celebration_mms_image_url\s*\}\}/gi, c.mmsUrl || '[celebration image unavailable — no token]')
    .replace(/\{\{\s*my_celebration_link\s*\}\}/gi, c.myCelebrationUrl || '[My Celebration link unavailable — no token]')
    // EXISTING party RSVP token only — the same link the backend sends. No sample,
    // no generated token. Missing token → a clear non-URL marker + UI warning.
    .replace(/\{\{\s*rsvp_link\s*\}\}/gi, rsvp);
}

function guestId(g) {
  return String(g?.id || g?._id || '');
}

// Render plain text with any http(s) URL as a clickable link that opens in a new
// tab — lets the host validate RSVP/invite links before sending (#3/#4).
function linkifyText(text) {
  const s = String(text == null ? '' : text);
  return s.split(/(https?:\/\/[^\s]+)/g).map((part, i) => (
    /^https?:\/\//.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ci-gold-hi)', textDecoration: 'underline', wordBreak: 'break-all' }}>{part}</a>
      : <React.Fragment key={i}>{part}</React.Fragment>
  ));
}
// Ensure anchors in sanitized HTML previews open in a new tab.
function openLinksInNewTab(html) {
  return String(html || '').replace(/<a\s/gi, '<a target="_blank" rel="noopener noreferrer" ');
}

// matchesSegment is imported from ciUtils (canonical, residual-pending). The
// previous inline copy positively listed pending/invited/no_response/none, which
// drifted from the backend (where pending = "neither Yes nor No"); removed.

function createCorrelationId() {
  return `ci-create-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// The audience channel the backend understands: 'sms' for texts, otherwise
// 'email' (an invitation is an email; notes never use this path).
function audienceChannel(type) {
  return type === 'sms' ? 'sms' : 'email';
}

// A stable campaign identity derived from the draft's subject so the
// campaign-aware backend can also exclude guests already contacted for THIS
// campaign (when sends are tagged with it). Falls back to a constant so the
// "already answered" exclusion still works even with an empty subject.
function campaignKeyFor(subject) {
  const slug = String(subject || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
  return slug ? `rsvp-pending:${slug}` : 'rsvp-pending';
}

// Deleted guests are a removed-from-roster bucket, not a real "we chose to skip
// them" exclusion — they must never inflate the audience base shown to the host.
// The backend tags them with a reason label; match it leniently (it has read
// "Guest is deleted" / "Deleted guest(s)" across versions) and always DISPLAY it
// as "Deleted guests" so the wording reads sensibly.
const DELETED_REASON_DISPLAY = 'Deleted guests';
function isDeletedReason(label) {
  return /\bdeleted\b/i.test(String(label || ''));
}

// Normalize the backend audience payload into one self-reconciling shape,
// regardless of whether it returns the rich {eligible_guests, counts:{total,
// eligible, excluded, reasons}} contract or the leaner {eligible, excluded_count,
// reasons/exclusions} contract. Yields:
//   { eligible:[guests], eligibleCount, excludedCount, deletedCount,
//     baseCount, reasons:[{label,count}] }
// where baseCount is the reconciling denominator with deleted guests removed,
// so eligibleCount + (excludedCount − deletedCount) === baseCount, and the
// "Deleted guests" bucket is split out (relabelled, never counted in the base).
function normalizePendingAudience(data) {
  if (!data || typeof data !== 'object') return null;
  const eligible = Array.isArray(data.eligible_guests)
    ? data.eligible_guests
    : (Array.isArray(data.eligible) ? data.eligible : []);
  const counts = data.counts || {};

  // Build the reason tally first — it's the source of truth for the deleted
  // split and (when an explicit excluded count is absent) for the excluded total.
  // Reasons can arrive as a {label: count} map, or as a list of strings/objects.
  let reasons = [];
  const reasonMap = counts.reasons || data.reasons;
  if (reasonMap && !Array.isArray(reasonMap) && typeof reasonMap === 'object') {
    reasons = Object.entries(reasonMap).map(([label, count]) => ({ label, count: Number(count) || 0 }));
  } else {
    const tally = {};
    const list = Array.isArray(reasonMap) ? reasonMap
      : (Array.isArray(data.exclusions) ? data.exclusions
        : (Array.isArray(data.excluded_guests) ? data.excluded_guests : []));
    list.forEach((item) => {
      const label = typeof item === 'string' ? item : (item?.reason || item?.label || 'Excluded');
      tally[label] = (tally[label] || 0) + 1;
    });
    reasons = Object.entries(tally).map(([label, count]) => ({ label, count }));
  }

  // Split out deleted guests (relabelled) and fold any duplicate deleted-style
  // labels into one "Deleted guests" line so the breakdown never double-lists.
  let deletedCount = 0;
  const realReasons = [];
  reasons.forEach((r) => {
    const count = Number(r.count) || 0;
    if (isDeletedReason(r.label)) deletedCount += count;
    else realReasons.push({ label: r.label, count });
  });
  realReasons.sort((a, b) => b.count - a.count);

  const excludedCount = Number(
    counts.excluded
    ?? data.excluded_count
    ?? (Array.isArray(data.excluded_guests) ? data.excluded_guests.length : undefined)
    ?? (Array.isArray(data.exclusions) ? data.exclusions.length : undefined)
    ?? (deletedCount + realReasons.reduce((s, r) => s + r.count, 0))
  ) || 0;
  const eligibleCount = Number(counts.eligible ?? eligible.length) || eligible.length || 0;

  // The clearly-stated base the host can reconcile against, with deleted guests
  // removed so they never inflate it. Prefer (eligible + non-deleted excluded);
  // fall back to a backend total minus deleted when the parts are unavailable.
  const reportedExcluded = Math.max(0, excludedCount - deletedCount);
  const baseFromParts = eligibleCount + reportedExcluded;
  const baseFromTotal = counts.total != null ? Math.max(0, Number(counts.total) - deletedCount) : null;
  const baseCount = baseFromTotal != null ? baseFromTotal : baseFromParts;

  return {
    eligible,
    eligibleCount,
    excludedCount,       // raw excluded incl. deleted (kept for compatibility)
    reportedExcluded,    // excluded WITHOUT deleted — what reconciles to base
    deletedCount,
    baseCount,
    reasons: realReasons,
  };
}

function escapeHtml(s) {
  return String(s).replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
}

// Plain text → simple HTML paragraphs (used to seed the rich editor from a
// plain-text starting draft, and to coerce SMS-style content into email HTML).
// Milo's drafting/enhance replies sometimes wrap the message in a preamble ("Here is
// your polished SMS…:") and/or append commentary ("📋 Context behind the draft…",
// markdown > quotes). That commentary must NEVER reach the sendable body. This splits
// message vs. commentary so the body holds only the message; the note shows separately.
// (Belt-and-suspenders for the FE until the agent returns {message, commentary} natively.)
function splitMiloMessage(raw) {
  let text = String(raw || '').replace(/\r\n/g, '\n').trim();
  const notes = [];
  // 1) Leading preamble line ("Here is …:", "Here's …:", "Below is …:", "I've drafted …:")
  const pre = text.match(/^(?:sure[,!.\s]*)?(?:here(?:'s| is| are)?|below is|i['’]ve (?:drafted|written|prepared)|of course[,!.\s]*here)\b[^\n]*:\s*\n+/i);
  if (pre) { notes.push(pre[0].replace(/:\s*$/, '').trim()); text = text.slice(pre[0].length).trim(); }
  // 2) Markdown blockquote lines are Milo commentary, never message body.
  const lines = text.split('\n');
  const quoted = lines.filter((l) => /^\s*>\s?/.test(l));
  if (quoted.length && quoted.length < lines.length) {
    notes.push(quoted.map((l) => l.replace(/^\s*>\s?/, '')).join('\n').trim());
    text = lines.filter((l) => !/^\s*>\s?/.test(l)).join('\n').replace(/\n{3,}/g, '\n\n').trim();
  }
  // 3) Trailing insight flagged by an emoji or explicit "Context behind the draft"/"Why this works".
  const tail = text.match(/\n+\s*(?:>\s*)?(?:(?:📋|💡|ℹ️|✅)[\s\S]*$|\*{0,2}(?:context behind the draft|why this works)\b[\s\S]*$)/i);
  if (tail) { notes.push(tail[0].trim()); text = text.slice(0, tail.index).trim(); }
  return { message: text.trim(), commentary: notes.filter(Boolean).join('\n\n').trim() };
}

// Honest SMS segmentation: GSM-7 = 160 (153 per part when multipart); UCS-2 (emoji /
// curly quotes) = 70 (67 multipart). Replaces the flat /160 that over-reported headroom.
function smsSegments(text) {
  const s = String(text || '');
  const len = [...s].length; // code-point aware (emoji-safe)
  const gsm = /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-.\/0-9:;<=>?¡A-ZÄÖÑÜ§¿a-zäöñüà€\[\]^{}\\|~]*$/;
  const unicode = !gsm.test(s);
  const single = unicode ? 70 : 160;
  const multi = unicode ? 67 : 153;
  if (len === 0) return { chars: 0, segments: 1, perSegment: single, unicode };
  if (len <= single) return { chars: len, segments: 1, perSegment: single, unicode };
  return { chars: len, segments: Math.ceil(len / multi), perSegment: single, unicode };
}

// Name the specific characters that forced a message into 70-char Unicode mode,
// so the host can fix them (each one alone collapses 160 → 70).
function unicodeCulprits(text) {
  const s = String(text || '');
  const out = [];
  if (/\p{Extended_Pictographic}|[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}️\u{1F1E6}-\u{1F1FF}]/u.test(s)) out.push('emoji');
  if (/[‒-―]/.test(s)) out.push('“—” em/en dash (use “-”)');
  if (/[‘’]/.test(s)) out.push('curly apostrophes ‘ ’ (use \')');
  if (/[“”]/.test(s)) out.push('curly quotes “ ” (use ")');
  if (/[…]/.test(s)) out.push('ellipsis … (use ...)');
  // Anything else outside GSM-7 that we didn't name explicitly.
  const gsm = /[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-.\/0-9:;<=>?¡A-ZÄÖÑÜ§¿a-zäöñüà€\[\]^{}\\|~]/;
  const named = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}️‒-―‘’“”…]|\p{Extended_Pictographic}/u;
  if ([...s].some((c) => !gsm.test(c) && !named.test(c))) out.push('other special characters');
  return out;
}

// Honest message strength — real factors (adequate length, personalization, RSVP CTA,
// subject for email, valid merge fields). NOT a non-empty check. Returns label + checks.
function evaluateStrength({ text, subject, type }) {
  const t = String(text || '').trim();
  const isEmail = type === 'email' || type === 'invitation';
  const checks = [];
  checks.push({ ok: t.length >= 15, text: t.length >= 15 ? 'Has a real message' : 'Too short — add more detail' });
  const personalized = /\{\{\s*(first_name|guest_name|name)\s*\}\}/i.test(t);
  checks.push({ ok: personalized, text: personalized ? 'Personalized with a merge tag' : 'Add {{first_name}} for a personal touch' });
  const hasRsvp = /\{\{\s*rsvp_link\s*\}\}/i.test(t) || /\brsvp\b/i.test(t);
  checks.push({ ok: hasRsvp, text: hasRsvp ? 'Clear RSVP call-to-action' : 'Add an RSVP link or a clear next step' });
  if (isEmail) checks.push({ ok: !!String(subject || '').trim(), text: String(subject || '').trim() ? 'Subject line set' : 'Add a subject line' });
  const tags = t.match(/\{\{[^}]*\}\}/g) || [];
  const known = /^\{\{\s*(first_name|last_name|guest_name|name|email|rsvp_link|my_celebration_link|event_name|event_date|venue|head_first_name|attending_guest_names|arrival_time|venue_name|venue_address|celebration_site_url|celebration_mms_image_url)\s*\}\}$/i;
  if (tags.length) { const bad = tags.some((x) => !known.test(x)); checks.push({ ok: !bad, text: bad ? 'Unknown merge field — check your tags' : 'Merge fields look valid' }); }
  const met = checks.filter((c) => c.ok).length, total = checks.length;
  let label = 'Add a message', color = 'var(--ci-txt3)';
  if (!t) { label = 'Add a message'; color = 'var(--ci-txt3)'; }
  else if (met >= total) { label = 'Looks strong'; color = 'var(--ci-green)'; }
  else if (met >= Math.ceil(total * 0.6)) { label = 'Getting there'; color = 'var(--ci-gold-hi)'; }
  else { label = 'Needs work'; color = '#c87d8e'; }
  return { label, color, checks, met, total };
}

function textToHtml(text) {
  const paras = String(text || '').split('\n').filter((l) => l.trim().length);
  if (!paras.length) return '';
  return paras.map((p) => `<p>${escapeHtml(p)}</p>`).join('\n');
}

// HTML → readable plain text (for the SMS body, char counting, and the
// auto-generated template name). Strips tags but keeps line breaks sane.
function htmlToText(html) {
  if (!html) return '';
  if (typeof document === 'undefined') return String(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const div = document.createElement('div');
  div.innerHTML = String(html)
    .replace(/<\/(p|div|h[1-6]|li)>/gi, '\n')
    .replace(/<br\s*\/?>(?!\n)/gi, '\n');
  const text = div.textContent || div.innerText || '';
  return text.replace(/\n{3,}/g, '\n\n').replace(/[ \t]+\n/g, '\n').trim();
}

const DEFAULT_HTML_BODY = "<p>We can't wait to celebrate this special day with you.</p>\n<p>Please RSVP by the date below.</p>";

// Local key for the resumable Create draft (Bug D). A draft is the full
// composition (type/subject/body/html/segment) — NOT a template. Per-browser,
// never auto-sends; restored via the "Resume draft" banner on return.
const CREATE_DRAFT_KEY = 'ci_create_draft';
const DEFAULT_SMS_BODY = "We can't wait to celebrate this special day with you.\nPlease RSVP by the date below.";
const DEFAULT_SUBJECT = "Let's celebrate together!";

// A stable, human-readable name for templates minted from a free draft.
function draftTemplateName({ subject, type, fallbackText, suffix }) {
  // Only email/invitation have a visible Subject field. For SMS/notes the
  // subject is a hidden default ("Let's celebrate together!") the host never
  // sees — so name those from the actual message text instead, or the row is
  // saved under an unrecognizable name.
  const base = (emailChannel(type) && subject && subject.trim())
    || (fallbackText && fallbackText.trim().slice(0, 48))
    || (type === 'sms' ? 'SMS draft' : type === 'note' ? 'Note draft' : 'Email draft');
  const stamp = new Date().toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  return `${base}${suffix ? ` (${suffix} · ${stamp})` : ` (${stamp})`}`;
}

// Self-contained modal backdrop (no dependency on another module's CSS).
const BACKDROP_STYLE = {
  position: 'fixed', inset: 0, zIndex: 9998,
  background: 'rgba(8, 8, 10, 0.62)', backdropFilter: 'blur(3px)',
  display: 'grid', placeItems: 'center', padding: 20, animation: 'ci-fade 0.2s',
};

export default function CreateScreen({ onBack, guests: guestsProp = null, onOpenOutbox = null, prefill = null }) {
  // Optional pre-fill (e.g. Outbox "Use again"). channel/type is preserved
  // exactly — an SMS row reopens as SMS, an email row as email — and the chosen
  // template is seeded too. We read prefill once via the lazy state initializers
  // below; the first-mount template-reset effect is suppressed so the seeded
  // template survives (see skipTemplateResetRef).
  const [type, setType] = useState(() => (prefill?.type === 'sms' ? 'sms' : (prefill?.type || 'email')));
  const [subject, setSubject] = useState(() => (prefill?.subject ?? DEFAULT_SUBJECT));
  // Plain-text body — the source of truth for SMS and internal notes.
  const [body, setBody] = useState(DEFAULT_SMS_BODY);
  // HTML body — the source of truth for email / invitation, edited via the
  // rich editor. Seeded from a prefill's stored HTML when present.
  const [htmlBody, setHtmlBody] = useState(() => (prefill?.htmlBody || DEFAULT_HTML_BODY));
  // Audience segment. Seeded from a prefill (Journey "Prepare in Send" or an
  // Event Readiness comms action) so the intended audience carries over; falls
  // back to "all" for a blank composition.
  const [segment, setSegment] = useState(() => (prefill?.segment || 'all'));
  const [wave, setWave] = useState('');

  // Live data
  const [guests, setGuests] = useState(Array.isArray(guestsProp) ? guestsProp : []);
  const [emailTpls, setEmailTpls] = useState([]);
  const [smsTpls, setSmsTpls] = useState([]);
  const [settings, setSettings] = useState(null);
  const [settingsErr, setSettingsErr] = useState('');
  // Event + celebration config for LIVE PREVIEW merge-tag resolution — the same
  // values the backend substitutes at send time ({{event_date}}, {{venue_name}},
  // {{arrival_time}}…). Load failures leave null → the preview shows honest
  // bracket markers instead of fabricated values.
  const [eventCfg, setEventCfg] = useState(null);
  const [celebrationCfg, setCelebrationCfg] = useState(null);
  const [templateId, setTemplateId] = useState(() => (prefill?.templateId ? String(prefill.templateId) : ''));

  // Audience: which guest ids are actually selected to receive this.
  const [selectedIds, setSelectedIds] = useState(null); // null = "follow the segment"; array = explicit custom set
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState('');
  // #8: in-Customize segment chips are a VIEW FILTER over the eligible list, not
  // a destructive re-seed of the manual selection. 'all' = show everyone; any
  // other key narrows what's DISPLAYED while the actual selectedIds persist
  // across filter switches. Reset alongside the modal so it never leaks state.
  const [audienceFilter, setAudienceFilter] = useState('all');
  // Cc / additional recipients (host 2026-07-16): guests appended ON TOP of the
  // segment or custom audience — works for email and SMS alike. Each added person
  // gets their own personalized copy (per-guest sends; no exposed address list).
  const [extraIds, setExtraIds] = useState([]);
  const [ccOpen, setCcOpen] = useState(false);
  const [ccSearch, setCcSearch] = useState('');
  // #7: read-only "View all recipients" roster modal + a transient "copied" flash.
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [viewAllSearch, setViewAllSearch] = useState('');
  const [rosterCopied, setRosterCopied] = useState(false);

  // Campaign-aware "Pending RSVP" audience (no-double-contact). Only the
  // pending preset uses this; it returns guests who haven't answered AND
  // haven't already been contacted for this campaign, plus an exclusion
  // breakdown. null = not loaded; we fall back to the client-side pending
  // filter on load/failure so the preset is never a dead end.
  const [pendingAudience, setPendingAudience] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState('');
  const [pendingExpanded, setPendingExpanded] = useState(false);

  // Review & schedule
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // {type, message, outboundId}

  // Save draft
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftResult, setDraftResult] = useState('');

  // Save as template (one-click persistence of the current draft)
  const [savingTpl, setSavingTpl] = useState(false);
  const [tplSaveResult, setTplSaveResult] = useState('');
  const [tplModal, setTplModal] = useState(null); // #6: { name } when the Save-as-Template modal is open

  // Milo enhance (tone rewrites)
  const [miloBusy, setMiloBusy] = useState(false);
  const [miloMsg, setMiloMsg] = useState(null); // {tone, text}
  const [miloNeedsKey, setMiloNeedsKey] = useState(false);

  // "Tell Milo what you want" — free-form drafting prompt → subject + body.
  const [miloPrompt, setMiloPrompt] = useState('');
  const [miloDraftBusy, setMiloDraftBusy] = useState(false);
  const [miloDraftNote, setMiloDraftNote] = useState('');

  const bodyRef = useRef(null);
  const correlationRef = useRef(createCorrelationId());
  // The [type] effect below clears the chosen template on every channel change.
  // On first mount that would wipe a prefilled template, so skip exactly once
  // when we mounted with a prefilled template.
  const skipTemplateResetRef = useRef(!!prefill?.templateId);

  // Email/invitation compose in HTML (htmlBody); SMS/note in plain text (body).
  const isHtmlChannel = emailChannel(type);
  // A plain-text view of the HTML body — used for char counts, validation, the
  // auto-template name, and as the Milo rewrite input for email.
  const htmlBodyText = useMemo(() => htmlToText(htmlBody), [htmlBody]);
  // The body actually sent / saved / validated for the active channel.
  const effectiveBody = isHtmlChannel ? htmlBody : body;
  const effectiveBodyText = isHtmlChannel ? htmlBodyText : body;

  // ---- Load live data -----------------------------------------------------
  useEffect(() => {
    let alive = true;
    if (!Array.isArray(guestsProp) || guestsProp.length === 0) {
      getGuests().then((d) => { if (alive) setGuests(Array.isArray(d) ? d : []); }).catch(() => {});
    }
    getEmailTemplates().then((d) => { if (alive) setEmailTpls(Array.isArray(d) ? d : []); }).catch(() => {});
    getSmsTemplates().then((d) => { if (alive) setSmsTpls(Array.isArray(d) ? d : []); }).catch(() => {});
    getOutboundSettings()
      .then((d) => { if (alive) { setSettings(d); setSettingsErr(''); } })
      .catch((e) => { if (alive) setSettingsErr(e.response?.data?.detail || 'Send settings are unavailable.'); });
    // Preview-only reads; failures degrade to bracket markers in the preview.
    getEventConfig().then((d) => { if (alive) setEventCfg(d || null); }).catch(() => {});
    getCelebrationConfig().then((d) => { if (alive) setCelebrationCfg(d || null); }).catch(() => {});
    return () => { alive = false; };
  }, [guestsProp]);

  // Close the merge-tag dropdown on any outside click.
  useEffect(() => {
    if (wave !== 'merge') return undefined;
    const close = () => setWave('');
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [wave]);

  // ---- Campaign-aware "Pending RSVP" audience -----------------------------
  // Only the pending preset uses the backend audience. It excludes guests who
  // already answered AND those already contacted for this campaign, and returns
  // an eligible/excluded breakdown. Re-fetch when the channel or campaign
  // identity (subject) changes; debounce so typing the subject doesn't spam it.
  // On failure we clear pendingAudience and fall back to the client-side filter.
  const campaignKey = useMemo(() => campaignKeyFor(subject), [subject]);
  // Channel decides contactability exclusions, so a stale cross-channel result
  // must never drive the audience. Clear it the instant the channel changes;
  // the client-side filter (which is channel-aware) covers the gap.
  useEffect(() => { setPendingAudience(null); setExtraIds([]); }, [type]);   // channel change: extras re-picked against the new channel's reachability
  useEffect(() => {
    if (segment !== 'pending' || type === 'note') {
      setPendingLoading(false);
      return undefined;
    }
    let alive = true;
    setPendingLoading(true);
    setPendingError('');
    const channel = audienceChannel(type);
    const handle = setTimeout(() => {
      getRsvpPendingAudience({ channel, campaign_key: campaignKey })
        .then((d) => {
          if (!alive) return;
          const norm = normalizePendingAudience(d);
          if (norm) { setPendingAudience(norm); setPendingError(''); }
          else { setPendingAudience(null); setPendingError('Could not load the live pending audience.'); }
        })
        .catch((e) => {
          if (!alive) return;
          // Graceful fallback: keep the preset working via the client-side filter.
          setPendingAudience(null);
          setPendingError(e.response?.data?.detail || 'Live pending audience is unavailable — using a local estimate.');
        })
        .finally(() => { if (alive) setPendingLoading(false); });
    }, 250);
    return () => { alive = false; clearTimeout(handle); };
  }, [segment, type, campaignKey]);

  // Collapse the exclusion detail whenever we leave the pending preset.
  useEffect(() => {
    if (segment !== 'pending') setPendingExpanded(false);
  }, [segment]);

  // Templates relevant to the active channel.
  // Safety filter keeps out system-protected LIVE templates and legacy
  // confirmation-typed rows — EXCEPT the celebration Confirmation MMS, which
  // the host sends through the normal Hub flow (identified by its celebration
  // merge tags so a real confirmation template is always selectable).
  const channelTemplates = useMemo(() => {
    const list = emailChannel(type) ? emailTpls : (type === 'sms' ? smsTpls : []);
    return (list || []).filter((t) =>
      !t.is_live && (t.template_type !== 'confirmation' || isConfirmationTemplate(t))
    );
  }, [type, emailTpls, smsTpls]);

  const selectedTemplate = useMemo(
    () => channelTemplates.find((t) => String(t.id || t._id) === templateId) || null,
    [channelTemplates, templateId]
  );

  // Reset the chosen template + send state whenever the channel changes.
  // Skip the very first run when we mounted with a prefilled template, so the
  // seeded template isn't wiped (the channel was preserved to match it).
  useEffect(() => {
    if (skipTemplateResetRef.current) {
      skipTemplateResetRef.current = false;
      return;
    }
    setTemplateId('');
    setSendResult(null);
    setDraftResult('');
    correlationRef.current = createCorrelationId();
  }, [type]);

  // ---- #6: load a chosen template's content into the composer --------------
  // Selecting a template in the dropdown only set `templateId`; the actual
  // subject/body never reached the editor. This effect loads it, but carefully:
  //  • Only on a genuine USER transition to a NON-EMPTY template (tracked via
  //    lastLoadedTemplateRef) — never on mount, channel-reset, or draft-restore.
  //  • Choosing "No template" (empty) must NOT wipe the composer, so we no-op.
  //  • Never clobbers in-progress NON-EMPTY content: a field is filled ONLY when
  //    it currently holds nothing but the channel default (or is blank). User
  //    edits are preserved; the template just fills the empty starting draft.
  //  • Prefill seeds its own body and is recorded as already-loaded on mount, so
  //    its content is never overwritten by this effect.
  const lastLoadedTemplateRef = useRef(prefill?.templateId ? String(prefill.templateId) : '');
  useEffect(() => {
    // Empty selection ("No template — send my draft"): keep the composer intact.
    if (!templateId) { lastLoadedTemplateRef.current = ''; return; }
    if (templateId === lastLoadedTemplateRef.current) return; // already applied
    if (!selectedTemplate) return; // template list not ready yet — wait for it
    lastLoadedTemplateRef.current = templateId;
    if (emailChannel(type)) {
      // Subject: only fill when blank or still the untouched default.
      const subjEmpty = !subject.trim() || subject === DEFAULT_SUBJECT;
      if (subjEmpty && typeof selectedTemplate.subject === 'string' && selectedTemplate.subject.trim()) {
        setSubject(selectedTemplate.subject);
      }
      // Body: only fill when blank or still the untouched default HTML.
      const htmlEmpty = !htmlToText(htmlBody) || htmlBody === DEFAULT_HTML_BODY;
      if (htmlEmpty && typeof selectedTemplate.html_content === 'string' && selectedTemplate.html_content.trim()) {
        setHtmlBody(selectedTemplate.html_content);
      }
    } else if (type === 'sms') {
      const bodyEmpty = !String(body).trim() || body === DEFAULT_SMS_BODY;
      if (bodyEmpty && typeof selectedTemplate.message_text === 'string' && selectedTemplate.message_text.trim()) {
        setBody(selectedTemplate.message_text);
      }
    }
    setSendResult(null);
  }, [templateId, selectedTemplate, type]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Audience resolution ------------------------------------------------
  const eligibleGuests = useMemo(() => guests.filter((g) => {
    if (!guestId(g)) return false;
    if (type === 'sms') return String(g.phone || '').trim() && !g.sms_opted_out;
    if (type === 'note') return true; // notes are internal; no contact requirement
    return String(g.email || '').trim();
  }).sort((a, b) => guestDisplayName(a).localeCompare(guestDisplayName(b), undefined, { sensitivity: 'base' })), [guests, type]);

  // True when the campaign-aware backend audience is driving the pending
  // preset (loaded successfully). When it isn't, the pending preset falls back
  // to the local client-side filter so it's never a dead end.
  const usingBackendPending = segment === 'pending' && type !== 'note' && !!pendingAudience;

  const segmentGuests = useMemo(() => {
    // Pending preset, backend audience available: use the campaign-aware
    // eligible list verbatim. Prefer the local guest object (so the Customize
    // modal, search, and previews share identity), but fall back to the
    // backend's own guest summary — it carries id/email/phone/name and is
    // fully compatible with the helpers used downstream.
    if (usingBackendPending) {
      const byId = new Map(eligibleGuests.map((g) => [guestId(g), g]));
      return (pendingAudience.eligible || [])
        .map((bg) => byId.get(guestId(bg)) || bg)
        .filter((g) => guestId(g));
    }
    // Every other preset (and the pending fallback) uses the client filter.
    return eligibleGuests.filter((g) => matchesSegment(g, segment));
  }, [usingBackendPending, pendingAudience, eligibleGuests, segment]);

  // The audience that will actually receive the message.
  const audienceGuests = useMemo(() => {
    const byId = new Map(eligibleGuests.map((g) => [guestId(g), g]));
    const base = selectedIds === null
      ? segmentGuests // following the segment
      : selectedIds.map((id) => byId.get(id)).filter(Boolean);
    if (!extraIds.length) return [...base].sort((a, b) => guestDisplayName(a).localeCompare(guestDisplayName(b), undefined, { sensitivity: 'base' }));
    const have = new Set(base.map(guestId));
    const extras = extraIds.filter((id) => !have.has(id)).map((id) => byId.get(id)).filter(Boolean);
    return [...base, ...extras].sort((a, b) => guestDisplayName(a).localeCompare(guestDisplayName(b), undefined, { sensitivity: 'base' }));
  }, [selectedIds, segmentGuests, eligibleGuests, extraIds]);
  const extraSet = useMemo(() => new Set(extraIds), [extraIds]);

  const audienceIds = useMemo(() => audienceGuests.map(guestId), [audienceGuests]);
  const audienceCount = audienceIds.length;
  const audienceLabel = (selectedIds === null
    ? (SEGMENTS.find((s) => s.key === segment)?.label || 'All Guests')
    : `Custom (${audienceCount})`) + (extraIds.length ? ` + ${extraIds.length} added` : '');

  // LIVE PREVIEW recipient switcher: step through the actual selected recipients
  // (Prev/Next) to spot-check each guest's personalization + real RSVP link before
  // sending. previewIndex can grow unbounded; previewPos wraps it into range so
  // removing recipients never lands on a stale slot.
  const [previewIndex, setPreviewIndex] = useState(0);
  const previewPos = audienceGuests.length
    ? (((previewIndex % audienceGuests.length) + audienceGuests.length) % audienceGuests.length)
    : 0;
  const prevPreview = () => setPreviewIndex((i) => i - 1);
  const nextPreview = () => setPreviewIndex((i) => i + 1);
  // The recipient being previewed: the selected one at previewPos; else any
  // eligible guest; else a synthetic sample so the preview is never raw {{tokens}}.
  const previewGuest = useMemo(
    () => audienceGuests[previewPos] || eligibleGuests[0] || { first_name: 'Alex', last_name: 'Rivera', email: 'alex@example.com' },
    [audienceGuests, eligibleGuests, previewPos],
  );
  const previewEventName = String(settings?.event_name || settings?.name || '').trim();

  // Celebration merge context for the PREVIEWED recipient — mirrors the backend's
  // _celebration_sms_merge_context: party primary = head, party members with a
  // YES rsvp = attending names, event/celebration config = date/venue/arrival,
  // and the party's EXISTING celebration token for the link + MMS image (never
  // generated here; missing token → honest bracket markers).
  const celPreviewCtx = useMemo(() => {
    const partyId = String(previewGuest?.party_id || '');
    const members = partyId
      ? guests.filter((g) => String(g?.party_id || '') === partyId && !g?.is_deleted)
      : [];
    const primary = members.find((g) => g?.is_primary) || null;
    const yes = members
      .filter((g) => /^(yes|confirmed)$/i.test(String(g?.rsvp_status || '').trim()))
      .sort((a, b) => ((b?.is_primary ? 1 : 0) - (a?.is_primary ? 1 : 0))
        || String(a?.first_name || '').localeCompare(String(b?.first_name || '')));
    const token = String(previewGuest?.party_celebration_token || '').trim();
    const dateRaw = String(eventCfg?.date || '').slice(0, 10);
    let eventDate = '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateRaw)) {
      const [y, m, d] = dateRaw.split('-').map(Number);
      eventDate = new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US',
        { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    }
    return {
      headFirst: String(primary?.first_name || previewGuest?.first_name || '').trim(),
      attendingNames: yes.map((g) => [g?.first_name, g?.last_name].filter(Boolean).join(' ').trim()).filter(Boolean).join(', '),
      eventDate,
      arrivalTime: String(celebrationCfg?.arrival_time || '').trim(),
      venueName: String(eventCfg?.venue_name || '').trim(),
      venueAddress: String(eventCfg?.venue_address || '').trim(),
      celUrl: token ? `${window.location.origin}/celebration/${token}` : '',
      mmsUrl: token ? `${window.location.origin}/api/celebration/mms/${token}.jpg` : '',
      myCelebrationUrl: previewGuest?.my_celebration_token ? `https://mycelebration.mvitations.com/${previewGuest.my_celebration_token}` : '',
    };
  }, [previewGuest, guests, eventCfg, celebrationCfg]);

  // SMS segment estimate computed on the RESOLVED send text (sample name + a
  // real-length RSVP URL), so it reflects what actually goes out — not the raw
  // {{token}} draft. Also surfaces which characters force 70-char Unicode mode.
  const smsResolved = useMemo(
    () => resolveMergeTagsForPreview(body, previewGuest, previewEventName, celPreviewCtx),
    [body, previewGuest, previewEventName, celPreviewCtx],
  );
  const smsEstimate = useMemo(() => smsSegments(smsResolved), [smsResolved]);
  const smsCulprits = useMemo(() => (smsEstimate.unicode ? unicodeCulprits(smsResolved) : []), [smsEstimate.unicode, smsResolved]);

  // MMS attachment preview: the selected template's media_urls with merge tags
  // resolved for the PREVIEWED recipient — the same substitution the backend
  // performs at send time, so the host sees the actual image that will attach.
  const previewMediaUrls = useMemo(() => {
    const raw = (selectedTemplate?.media_urls || []).map((u) => String(u || ''));
    if (!raw.length) return [];
    return raw
      .map((u) => u.replace(/\{\{\s*celebration_mms_image_url\s*\}\}/gi, celPreviewCtx.mmsUrl || ''))
      .map((u) => u.trim())
      .filter((u) => /^https?:\/\//.test(u));
  }, [selectedTemplate, celPreviewCtx]);
  const previewMediaMissing = (selectedTemplate?.media_urls || []).length > 0 && previewMediaUrls.length === 0;

  // RSVP link presence (source of truth: the recipient's EXISTING party token).
  const previewRsvpLink = useMemo(() => rsvpLinkForGuest(previewGuest), [previewGuest]);
  const usesRsvpLink = useMemo(
    () => /\{\{\s*rsvp_link\s*\}\}/i.test(emailChannel(type) ? htmlBody : body),
    [type, htmlBody, body],
  );
  const previewTokenMissing = usesRsvpLink && !previewRsvpLink;

  // ---- Auto-preserved draft (Bug D) -------------------------------------
  // The in-progress composition is continuously kept in localStorage so moving
  // between Communications sections (or a hard refresh) never loses it. It's a
  // DRAFT, not a template, and never auto-sends. A force-close may lose the very
  // last keystroke (acceptable). Cleared on send or "Start fresh".
  const meaningfulDraft = (t, subj, b, html) => emailChannel(t)
    ? Boolean((htmlToText(html) && html !== DEFAULT_HTML_BODY) || (subj && subj.trim() && subj !== DEFAULT_SUBJECT))
    : Boolean(String(b || '').trim() && b !== DEFAULT_SMS_BODY);
  const [draftRestored, setDraftRestored] = useState(false);
  const draftHydrated = useRef(false);
  // Restore re-renders trigger the persist effect once with the just-applied
  // state; skip that single run so it can't clobber draftRestored / re-clear.
  const skipNextAutosave = useRef(false);
  // Restore any saved draft once on mount (skip when a prefill seeded the screen).
  useEffect(() => {
    if (prefill) { draftHydrated.current = true; return; }
    try {
      const raw = window.localStorage.getItem(CREATE_DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (meaningfulDraft(d?.type, d?.subject, d?.body, d?.htmlBody)) {
          if (d.type) setType(d.type);
          if (typeof d.subject === 'string') setSubject(d.subject);
          if (typeof d.body === 'string') setBody(d.body);
          if (typeof d.htmlBody === 'string') setHtmlBody(d.htmlBody);
          if (d.segment) setSegment(d.segment);
          // Restore the custom recipient selection too — the audience is part of
          // the communication. null = follow segment; array = explicit custom set.
          if (Array.isArray(d.selectedIds) || d.selectedIds === null) setSelectedIds(d.selectedIds);
          setDraftRestored(true);
          skipNextAutosave.current = true;
        }
      }
    } catch { /* ignore */ }
    draftHydrated.current = true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Continuously persist the composition after hydration; clear when blank.
  useEffect(() => {
    if (!draftHydrated.current || prefill) return;
    if (skipNextAutosave.current) { skipNextAutosave.current = false; return; }
    try {
      if (meaningfulDraft(type, subject, body, htmlBody)) {
        window.localStorage.setItem(CREATE_DRAFT_KEY, JSON.stringify({ type, subject, body, htmlBody, segment, selectedIds, savedAt: new Date().toISOString() }));
      } else {
        window.localStorage.removeItem(CREATE_DRAFT_KEY);
        setDraftRestored(false);
      }
    } catch { /* ignore */ }
  }, [type, subject, body, htmlBody, segment, selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps
  const clearDraft = () => {
    try { window.localStorage.removeItem(CREATE_DRAFT_KEY); } catch { /* ignore */ }
    setDraftRestored(false);
  };
  const startFresh = () => {
    setSubject(DEFAULT_SUBJECT);
    setBody(DEFAULT_SMS_BODY);
    setHtmlBody(DEFAULT_HTML_BODY);
    clearDraft();
    setDraftResult('');
  };

  // Honest reachability clarifier. Three denominators legitimately differ here —
  // the raw guest list, who is reachable in THIS channel, and (for the pending
  // preset) the backend's campaign exclusions. The recipient count above is the
  // real send audience; this line just names the channel-reachability gap so it
  // reads as informative ("N of M are SMS-reachable") rather than contradictory.
  // Notes have no contact requirement, so the gap is meaningless there.
  const totalOnList = guests.filter((g) => guestId(g)).length;
  const reachableCount = eligibleGuests.length; // channel-eligible (real field)
  const reachableWord = type === 'sms' ? 'SMS-reachable' : 'email-reachable';
  // The TRUE size of the chosen segment over the WHOLE guest list (everyone,
  // reachable or not) — so "All Guests" shows the real total and matches the
  // Guests tab instead of collapsing to the channel-reachable subset.
  const segmentTotal = useMemo(
    () => guests.filter((g) => guestId(g) && matchesSegment(g, segment)).length,
    [guests, segment],
  );
  // Headline count: the full segment when following it; the explicit set when custom.
  const displayCount = selectedIds === null ? segmentTotal : audienceCount;
  // Surface the reachability gap only when one actually exists.
  const showReachability = type !== 'note' && displayCount > 0 && audienceCount < displayCount;

  // When segment changes while not customized, the audience just follows it.
  // When the user opens "Customize Audience", we seed the explicit set from the
  // current segment so add/remove starts from a sensible place.
  const openCustomize = () => {
    if (selectedIds === null) setSelectedIds(segmentGuests.map(guestId));
    setAudienceFilter('all'); // #8: open showing everyone; chips only filter the view
    setAudienceOpen(true);
  };
  const resetToSegment = () => {
    setSelectedIds(null);
    setRecipientSearch('');
    setAudienceFilter('all');
    setExtraIds([]);
  };
  const toggleRecipient = (id) => {
    // An added (Cc) recipient is removed from the extras list — never converts
    // the whole audience to a custom set just to drop one addition.
    if (extraSet.has(id)) { setExtraIds((cur) => cur.filter((x) => x !== id)); setSendResult(null); return; }
    setSelectedIds((cur) => {
      const base = cur === null ? segmentGuests.map(guestId) : cur;
      return base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    });
    setSendResult(null);
  };
  const toggleExtra = (id) => {
    setExtraIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
    setSendResult(null);
  };

  const filteredEligible = useMemo(() => {
    const q = recipientSearch.trim().toLowerCase();
    const eligibleIds = new Set(eligibleGuests.map(guestId));
    const matchesSearch = (g) => {
      if (!q) return true;
      return guestDisplayName(g).toLowerCase().includes(q)
        || String(g.email || '').toLowerCase().includes(q)
        || String(g.phone || '').toLowerCase().includes(q);
    };
    // #8: the in-Customize segment chips are a VIEW FILTER, not a re-seed. They
    // narrow only what's DISPLAYED here (alongside the search box); the manual
    // selectedIds set is untouched, so selections persist across filter switches.
    // 'all' shows everyone (canonical matchesSegment treats 'all' as pass-through).
    const matchesViewFilter = (g) => matchesSegment(g, audienceFilter);
    // Show the WHOLE guest list so "All Guests" really means everyone and matches
    // the Guests tab. A guest who isn't reachable on THIS channel (no email for
    // email, no phone for SMS) still appears — greyed, with a reason, and disabled
    // so it can't be added — instead of being hidden (which made "55" look like the
    // list was truncated). Reachable first for a sensible order.
    return guests
      .filter((g) => guestId(g) && matchesSearch(g) && matchesViewFilter(g))
      .map((g) => {
        const reachable = eligibleIds.has(guestId(g));
        const why = reachable ? '' : (type === 'sms'
          ? (String(g.phone || '').trim() ? 'opted out of SMS' : 'no phone number')
          : type === 'note' ? '' : (String(g.email || '').trim() ? 'unavailable' : 'no email address'));
        return { ...g, _reachable: reachable, _why: why };
      })
      .sort((a, b) => (a._reachable === b._reachable
        ? guestDisplayName(a).localeCompare(guestDisplayName(b), undefined, { sensitivity: 'base' })
        : a._reachable ? -1 : 1))
      .slice(0, 500);
  }, [guests, eligibleGuests, recipientSearch, type, audienceFilter]);

  // ---- Merge tags / RSVP insertion ---------------------------------------
  // For email/invitation the body lives in the RichEditor (contentEditable):
  // insert at the caret via execCommand if it's focused, else append to the
  // HTML. For SMS/note we insert into the plain-text textarea at the caret.
  const insertToken = (token) => {
    if (isHtmlChannel) {
      const editor = document.querySelector('.ci-create-grid .re-edit');
      const sel = window.getSelection && window.getSelection();
      const focused = editor && sel && sel.rangeCount > 0 && editor.contains(sel.anchorNode);
      if (focused) {
        editor.focus();
        try { document.execCommand('insertText', false, token); } catch { /* noop */ }
        setHtmlBody(editor.innerHTML);
      } else {
        setHtmlBody((h) => `${h || ''}<span>${token}</span>`);
      }
      setSendResult(null);
      return;
    }
    const el = bodyRef.current;
    if (!el) { setBody((b) => `${b}${b && !b.endsWith(' ') ? ' ' : ''}${token}`); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = `${body.slice(0, start)}${token}${body.slice(end)}`;
    setBody(next);
    // restore caret just after the inserted token
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      } catch { /* noop */ }
    });
    setSendResult(null);
  };

  // Insert ready-made HTML (e.g. a styled RSVP button) at the caret in the rich
  // editor. Email/invitation only — plain-text channels use insertToken. Nothing
  // is auto-injected: the host opts in via the toolbar, then can edit, restyle,
  // move, or delete the result like any other content.
  const insertHtml = (html) => {
    const editor = document.querySelector('.ci-create-grid .re-edit');
    const sel = window.getSelection && window.getSelection();
    const focused = editor && sel && sel.rangeCount > 0 && editor.contains(sel.anchorNode);
    if (focused) {
      editor.focus();
      try { document.execCommand('insertHTML', false, html); } catch { /* noop */ }
      setHtmlBody(editor.innerHTML);
    } else {
      setHtmlBody((h) => `${h || ''}${html}`);
    }
    setSendResult(null);
  };

  // ---- Safety gate (mirrors SendPanel.submitBlockedReason) ----------------
  // Note: a pre-made template is NOT required. If none is selected we auto-mint
  // an inactive template from the draft at send time (handleConfirmSend) and go
  // through the SAME previewOutbound* → sendEmails/sendSms gate — the safety
  // path is never bypassed, the user just isn't forced to template-first.
  const sendBlockedReason = useMemo(() => {
    if (type === 'note') return 'Internal notes are saved as drafts, not sent. Use Save Draft.';
    if (type === 'invitation' && !emailChannel(type)) return '';
    if (!settings) return settingsErr || 'Send settings are still loading.';
    if (!settings.safety_enabled) return 'Sending is disabled in settings.';
    if (type === 'sms' && !settings.sms_provider_enabled) return 'SMS is disabled until the provider is configured and enabled.';
    if (!audienceCount) return `Select at least one ${type === 'sms' ? 'SMS-ready' : 'email'} recipient.`;
    if (!effectiveBodyText.trim()) return 'Message body is required.';
    if (emailChannel(type) && !subject.trim()) return 'Subject is required.';
    return '';
  }, [type, settings, settingsErr, audienceCount, effectiveBodyText, subject]);

  const sendMode = settings?.send_mode || 'hold';
  const modeLabel = sendMode === 'live' ? 'Live Send' : sendMode === 'test' ? 'Test Mode' : 'Hold for Review';
  const sendVerb = sendMode === 'live' ? 'Send' : sendMode === 'test' ? 'Send Test' : 'Queue';

  const openReview = () => {
    setSendResult(null);
    if (sendBlockedReason) { setWave('blocked'); return; }
    setConfirmOpen(true);
  };

  // ---- The real, safe send -----------------------------------------------
  // A pre-made template is optional. When none is selected we AUTO-CREATE an
  // inactive template (is_active:false) from the current draft, then run the
  // EXACT same previewOutbound* → sendEmails/sendSms gate with that template id.
  // The safety gate is never bypassed — free drafts simply mint their own
  // template behind the scenes so they can flow through it.
  const handleConfirmSend = async () => {
    if (sendBlockedReason || sending) return;
    setSending(true);
    setSendResult(null);
    const isSms = type === 'sms';
    const correlation = correlationRef.current;
    const sendBody = effectiveBody; // HTML for email, plain text for SMS
    try {
      // 0) Ensure a template id exists for the gate (auto-mint if free draft).
      let effectiveTemplateId = templateId;
      if (!effectiveTemplateId) {
        const autoName = draftTemplateName({ subject, type, fallbackText: effectiveBodyText, suffix: 'auto' });
        if (isSms) {
          const created = await createSmsTemplate({ name: autoName, template_type: 'custom', message_text: body });
          effectiveTemplateId = String(created?.id || created?._id || '');
          if (smsTpls) setSmsTpls((list) => [created, ...(list || [])]);
        } else {
          const created = await createEmailTemplate({
            name: autoName,
            subject: subject || '',
            template_type: type === 'invitation' ? 'invitation' : 'custom',
            html_content: htmlBody || '<p></p>',
            is_active: false,
          });
          effectiveTemplateId = String(created?.id || created?._id || '');
          if (emailTpls) setEmailTpls((list) => [created, ...(list || [])]);
        }
        if (!effectiveTemplateId) {
          setSendResult({ type: 'failed', message: 'Could not prepare a template for this draft. Try “Save as template”, then send.' });
          setSending(false);
          return;
        }
        // Reflect the minted template in the UI so the confirm summary is honest.
        setTemplateId(effectiveTemplateId);
      }
      // 1) Preview FIRST to mint the stale-preview digest the gate requires.
      const previewPayload = {
        template_id: effectiveTemplateId,
        guest_ids: audienceIds,
        custom_body: sendBody,
        correlation_id: correlation,
        source: 'communications_create',
      };
      const preview = isSms
        ? await previewOutboundSms(previewPayload)
        : await previewOutboundEmail({ ...previewPayload, custom_subject: subject, confirm_bulk_send: true });
      if (preview?.error) {
        setSendResult({ type: 'failed', message: preview.error });
        setSending(false);
        return;
      }
      const digest = preview?.preview_digest;
      // 2) Send through the SAME safe path SendPanel uses (respects send_mode).
      const result = isSms
        ? await sendSms(effectiveTemplateId, audienceIds, {
          custom_body: sendBody,
          correlation_id: correlation,
          preview_digest: digest,
          source: 'communications_create',
        })
        : await sendEmails(effectiveTemplateId, audienceIds, [], [], true, {
          custom_subject: subject,
          custom_body: sendBody,
          correlation_id: correlation,
          preview_digest: digest,
          source: 'communications_create',
        });
      const status = result?.status === 'failed' ? 'failed' : (result?.status || 'queued');
      setSendResult({
        type: status,
        message: result?.message
          || (sendMode === 'live' ? `Sent to ${audienceCount} recipient(s).`
            : sendMode === 'test' ? `Test send recorded for ${audienceCount} recipient(s).`
              : `Queued ${audienceCount} message(s) for review in the Outbox.`),
        outboundId: result?.outbound_message_id || null,
      });
      setConfirmOpen(false);
      if (status !== 'failed') clearDraft(); // sent/queued — the draft is no longer pending
      correlationRef.current = createCorrelationId(); // fresh id for the next send
    } catch (e) {
      const detail = e.response?.data?.detail || 'The communication could not be sent.';
      const providerDisabled = e.response?.status === 503 || String(detail).toLowerCase().includes('provider');
      setSendResult({ type: providerDisabled ? 'provider_disabled' : 'failed', message: detail });
    } finally {
      setSending(false);
    }
  };

  // ---- Save draft (real persistence) -------------------------------------
  // Drafts persist as INACTIVE templates (is_active:false) so they never
  // auto-send. Email/invitation/note store the rich HTML body verbatim; SMS
  // stores the plain message text.
  const handleSaveDraft = async () => {
    if (savingDraft) return;
    setSavingDraft(true);
    setDraftResult('');
    try {
      // Explicit snapshot (auto-save already keeps the draft continuously; this
      // is the user-facing confirmation). A DRAFT, not a template; never sends.
      window.localStorage.setItem(CREATE_DRAFT_KEY, JSON.stringify({ type, subject, body, htmlBody, segment, selectedIds, savedAt: new Date().toISOString() }));
      setDraftResult('Draft saved — message and recipients stay here as you move around Communications and after a refresh.');
    } catch (e) {
      setDraftResult('Could not save the draft right now.');
    } finally {
      setSavingDraft(false);
    }
  };

  // ---- Save as template (one-click; a real, reusable, ACTIVE-eligible row) -
  // Unlike Save Draft (inactive), this persists the current draft as a normal
  // template the user can pick later. Email saves as an active-eligible custom
  // template; SMS saves the message text.
  // Suggested template name from the channel-appropriate content (editable in the modal).
  const suggestTemplateName = () => {
    const base = emailChannel(type)
      ? (subject?.trim() || htmlToText(htmlBody).slice(0, 48))
      : String(body || '').trim().slice(0, 48);
    return (base || (type === 'sms' ? 'SMS template' : type === 'note' ? 'Note template' : 'Email template')).trim();
  };
  // Open the naming modal (#6) — host names the template before saving.
  const openSaveTemplate = () => {
    if (!effectiveBodyText.trim()) { setTplSaveResult('Add a message first.'); return; }
    setTplSaveResult('');
    setTplModal({ name: suggestTemplateName() });
  };
  const handleSaveAsTemplate = async (chosenName) => {
    if (savingTpl) return;
    if (!effectiveBodyText.trim()) { setTplSaveResult('Add a message first.'); return; }
    const name = (chosenName && chosenName.trim()) || suggestTemplateName();
    setSavingTpl(true);
    setTplSaveResult('');
    try {
      if (type === 'sms') {
        const created = await createSmsTemplate({ name, template_type: 'custom', message_text: body });
        if (smsTpls) setSmsTpls((list) => [created, ...(list || [])]);
        if (created?.id || created?._id) setTemplateId(String(created.id || created._id));
      } else {
        const created = await createEmailTemplate({
          name,
          subject: subject || '',
          template_type: type === 'invitation' ? 'invitation' : 'custom',
          html_content: htmlBody || '<p></p>',
          is_active: false,
        });
        if (emailTpls) setEmailTpls((list) => [created, ...(list || [])]);
        if (created?.id || created?._id) setTemplateId(String(created.id || created._id));
      }
      setTplModal(null);
      setTplSaveResult(`Saved as ${type === 'sms' ? 'an SMS' : 'an email'} template (find it under Templates${type === 'sms' ? ' › SMS' : ''}) — selected for this send.`);
    } catch (e) {
      setTplSaveResult(e.response?.data?.detail || 'Could not save the template.');
    } finally {
      setSavingTpl(false);
    }
  };

  // ---- Enhance with Milo (real call; graceful no-key) ---------------------
  const enhanceWithMilo = async (tone) => {
    if (miloBusy) return;
    setMiloBusy(true);
    setMiloMsg(null);
    setMiloNeedsKey(false);
    const channelWord = type === 'sms' ? 'SMS' : type === 'invitation' ? 'invitation' : type === 'note' ? 'internal note' : 'email';
    const question = `Rewrite this event ${channelWord} to be ${tone}. Keep any {{merge_tags}} and ${RSVP_TOKEN} exactly as written. Return only the rewritten message body.\n\nSubject: ${subject}\n\nBody:\n${effectiveBodyText}`;
    try {
      const res = await askMiloAgent(question);
      // Graceful no-key path: backend returns refusal_reason:'no_llm_key'.
      if (res?.refusal_reason === 'no_llm_key') {
        setMiloNeedsKey(true);
      } else if (res?.refusal_reason) {
        setMiloMsg({ tone, text: res.answer || 'Milo could not rewrite this right now.' });
      } else {
        const { message, commentary } = splitMiloMessage(res?.answer || '');
        setMiloMsg({ tone, text: message, commentary });
      }
    } catch (e) {
      const status = e.response?.status;
      if (status === 401 || status === 403) {
        setMiloNeedsKey(true);
      } else {
        setMiloMsg({ tone, text: e.response?.data?.detail || 'Milo is unavailable right now.' });
      }
    } finally {
      setMiloBusy(false);
    }
  };

  const applyMilo = () => {
    if (!miloMsg?.text) return;
    // For email/invitation, fold the plain-text rewrite into HTML paragraphs so
    // it lands cleanly in the rich editor; SMS/note keep it as plain text.
    if (isHtmlChannel) setHtmlBody(textToHtml(miloMsg.text));
    else setBody(miloMsg.text);
    setMiloMsg(null);
    setSendResult(null);
  };

  // ---- "Tell Milo what you want" — free-form draft → subject + body --------
  // The user types what they want; Milo drafts both a subject line and the body
  // straight into the editor. We ask for a clearly delimited SUBJECT / BODY so
  // we can split reliably; if Milo doesn't, the whole reply becomes the body.
  const draftWithMilo = async () => {
    if (miloDraftBusy) return;
    const want = miloPrompt.trim();
    if (!want) { setMiloDraftNote('Tell Milo what you want to say first.'); return; }
    setMiloDraftBusy(true);
    setMiloDraftNote('');
    setMiloNeedsKey(false);
    const channelWord = type === 'sms' ? 'SMS text message' : type === 'invitation' ? 'event invitation email' : type === 'note' ? 'internal note' : 'event email';
    const wantsSubject = emailChannel(type);
    const fmt = wantsSubject
      ? `Respond in EXACTLY this format and nothing else:\nSUBJECT: <one short subject line>\nBODY:\n<the message body>`
      : `Return only the message body text, with no preamble.`;
    const question = `Draft a warm, polished ${channelWord} for a high-end event. The host wants: "${want}". Use {{first_name}} where a personal greeting helps and ${RSVP_TOKEN} where an RSVP call-to-action belongs. ${fmt}`;
    try {
      const res = await askMiloAgent(question);
      if (res?.refusal_reason === 'no_llm_key') { setMiloNeedsKey(true); return; }
      const answer = String(res?.answer || '').trim();
      if (!answer) { setMiloDraftNote('Milo returned an empty draft — try rephrasing.'); return; }
      if (res?.refusal_reason) { setMiloDraftNote(answer); return; }
      // Parse SUBJECT/BODY for email; otherwise treat the whole reply as body.
      let draftSubject = '';
      let draftBody = answer;
      if (wantsSubject) {
        const m = answer.match(/SUBJECT:\s*(.*?)\s*(?:\n+)BODY:\s*([\s\S]*)$/i);
        if (m) { draftSubject = m[1].trim(); draftBody = m[2].trim(); }
        else {
          const sm = answer.match(/^SUBJECT:\s*(.*)$/im);
          if (sm) { draftSubject = sm[1].trim(); draftBody = answer.replace(sm[0], '').replace(/^BODY:\s*/im, '').trim(); }
        }
      }
      if (draftSubject) setSubject(draftSubject);
      // Keep Milo's preamble/insights OUT of the sendable body — surface them as a note.
      const { message: cleanBody, commentary } = splitMiloMessage(draftBody);
      if (isHtmlChannel) setHtmlBody(textToHtml(cleanBody));
      else setBody(cleanBody);
      setSendResult(null);
      setMiloDraftNote(commentary
        ? `Milo drafted this — review and edit freely.\n\nMilo's note (kept out of the message):\n${commentary}`
        : 'Milo drafted this — review and edit freely.');
    } catch (e) {
      const status = e.response?.status;
      if (status === 401 || status === 403) setMiloNeedsKey(true);
      else setMiloDraftNote(e.response?.data?.detail || 'Milo is unavailable right now.');
    } finally {
      setMiloDraftBusy(false);
    }
  };

  // Merge-tag + RSVP-link buttons injected into the rich editor's toolbar.
  const mergeExtras = (
    <MergeTagButtons onInsert={insertToken} onInsertHtml={insertHtml} />
  );

  return (
    <div className="ci-view">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <button className="ci-btn ci-sm" onClick={onBack}><ArrowLeft size={13} /> Back</button>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {(tplSaveResult || draftResult) && <span style={{ fontSize: 12, color: 'var(--ci-txt2)' }}>{tplSaveResult || draftResult}</span>}
          {type !== 'note' && (
            <button className="ci-btn ci-sm" onClick={openSaveTemplate} disabled={savingTpl} title="Persist this draft as a reusable template">
              <Save size={13} /> {savingTpl ? 'Saving…' : 'Save as template'}
            </button>
          )}
          <button className="ci-btn ci-sm" onClick={handleSaveDraft} disabled={savingDraft}>
            {savingDraft ? 'Saving…' : 'Save Draft'}
          </button>
          <button className="ci-btn ci-gold ci-sm" onClick={openReview}>Review &amp; {sendVerb} →</button>
        </div>
      </div>

      {/* Auto-preserved draft (Bug D): the in-progress composition was restored. */}
      {draftRestored && !prefill && (
        <div className="ci-glass" style={{ padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, borderColor: 'var(--ci-bdgold)' }}>
          <FileText size={14} style={{ color: 'var(--ci-gold-hi)', flex: 'none' }} />
          <span style={{ fontSize: 12.5, color: 'var(--ci-txt2)', flex: 1 }}>
            Continuing your saved draft — your edits are kept as you move around Communications and across a refresh.
          </span>
          <button className="ci-btn ci-sm" onClick={startFresh}>Start fresh</button>
        </div>
      )}

      {prefill?.intent && (
        <div className="ci-glass" style={{ padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, borderColor: 'var(--ci-bdgold)' }}>
          <Sparkles size={14} style={{ color: 'var(--ci-gold-hi)', flex: 'none' }} />
          <span style={{ fontSize: 12.5, color: 'var(--ci-txt2)' }}>Prepared from <b style={{ color: 'var(--ci-txt)' }}>{prefill.intent}</b> — channel, template, and audience are preset below. Edit anything before sending.</span>
        </div>
      )}

      {/* Send result banner (success / queued / failed) */}
      {sendResult && (
        <div className="ci-strength" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
          {sendResult.type === 'failed' || sendResult.type === 'provider_disabled'
            ? <AlertCircle size={18} style={{ color: 'var(--ci-gold-hi)', flex: 'none' }} />
            : <ShieldCheck size={18} style={{ color: 'var(--ci-green)', flex: 'none' }} />}
          <span style={{ fontSize: 13, color: 'var(--ci-txt)' }}>{sendResult.message}</span>
          {sendResult.outboundId && onOpenOutbox && (
            <button className="ci-btn ci-sm" style={{ marginLeft: 'auto' }} onClick={onOpenOutbox}>View in Outbox</button>
          )}
        </div>
      )}

      <div className="ci-create-grid">
        {/* LEFT — config */}
        <div className="ci-glass" style={{ padding: 18 }}>
          <div className="ci-lab" style={{ marginTop: 0 }}>Communication Type</div>
          <div className="ci-seg">
            {TYPES.map(({ key, label, Icon }) => (
              <span key={key} className={`ci-s ${type === key ? 'ci-on' : ''}`} onClick={() => setType(key)}>
                <Icon size={13} style={{ verticalAlign: -2, marginRight: 4 }} />{label}
              </span>
            ))}
          </div>

          <div className="ci-lab">Audience</div>
          <select
            className="ci-inp"
            value={segment}
            onChange={(e) => { setSegment(e.target.value); resetToSegment(); }}
            disabled={selectedIds !== null}
            title={selectedIds !== null ? 'Custom audience active — reset to use a segment' : undefined}
          >
            {SEGMENTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--ci-txt2)' }}>
              <Users size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
              <b style={{ color: 'var(--ci-txt)' }}>{displayCount}</b> {selectedIds === null ? 'guest' : 'recipient'}{displayCount === 1 ? '' : 's'} · {audienceLabel}
            </span>
            <span style={{ display: 'flex', gap: 6 }}>
              {type !== 'note' && (
                <button className="ci-btn ci-sm ci-ghost" onClick={() => { setCcSearch(''); setCcOpen(true); }} title="Add extra recipients on top of this audience — each gets their own copy">+ Cc / add recipients</button>
              )}
              <button className="ci-btn ci-sm ci-ghost" onClick={openCustomize}>Customize Audience</button>
            </span>
          </div>
          {/* Recipient box (#5): scrollable name + remove rows — verify/trim WHO
              gets the message without reopening Customize Audience. ~6 rows then scrolls. */}
          {audienceCount > 0 && type !== 'note' && (
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--ci-txt3)' }}>Recipients ({audienceCount})</span>
                {/* #7: read-only full roster (name + email/phone, search, copy all). */}
                <button
                  className="ci-btn ci-sm ci-ghost"
                  style={{ fontSize: 11, padding: '2px 8px' }}
                  onClick={() => { setViewAllSearch(''); setRosterCopied(false); setViewAllOpen(true); }}
                  title="See the full recipient list"
                >View all recipients</button>
              </div>
              <div style={{ border: '1px solid var(--ci-bd)', borderRadius: 10, maxHeight: 200, overflowY: 'auto', background: 'rgba(255,255,255,.02)' }}>
                {audienceGuests.map((g, i) => (
                  <div key={guestId(g)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 10px', borderTop: i === 0 ? 'none' : '1px solid rgba(255,255,255,.06)' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--ci-txt)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {guestDisplayName(g)}
                      {extraSet.has(guestId(g)) && <span style={{ marginLeft: 6, fontSize: 9, letterSpacing: '.08em', color: 'var(--ci-gold-hi)', border: '1px solid var(--ci-bd)', borderRadius: 999, padding: '1px 6px', verticalAlign: 1 }}>CC</span>}
                    </span>
                    <button
                      onClick={() => toggleRecipient(guestId(g))}
                      title={`Remove ${guestDisplayName(g)}`}
                      style={{ flex: 'none', width: 22, height: 22, borderRadius: 6, border: '1px solid var(--ci-bd)', background: 'transparent', color: 'var(--ci-txt3)', cursor: 'pointer', display: 'grid', placeItems: 'center', fontSize: 11, lineHeight: 1 }}
                    >✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {showReachability && (
            <div style={{ fontSize: 11, color: 'var(--ci-txt3)', marginTop: 4, display: 'flex', alignItems: 'flex-start', gap: 5 }}>
              <Info size={11} style={{ flex: 'none', marginTop: 1 }} />
              <span>
                <b style={{ color: 'var(--ci-txt2)' }}>{audienceCount}</b> of {displayCount} {audienceCount === 1 ? 'is' : 'are'} {reachableWord} and will receive this — the other {displayCount - audienceCount} {(displayCount - audienceCount) === 1 ? 'is' : 'are'} shown greyed ({type === 'sms' ? 'no phone' : 'no email'}) and excluded.
              </span>
            </div>
          )}
          {selectedIds !== null && (
            <button className="ci-btn ci-sm ci-ghost" style={{ marginTop: 6 }} onClick={resetToSegment}>Reset to segment</button>
          )}

          {/* Pending RSVP — campaign-aware eligibility summary (no double-contact). */}
          {segment === 'pending' && type !== 'note' && (
            <PendingAudienceSummary
              loading={pendingLoading}
              error={pendingError}
              audience={pendingAudience}
              sendCount={audienceCount}
              customized={selectedIds !== null}
              expanded={pendingExpanded}
              onToggle={() => setPendingExpanded((v) => !v)}
            />
          )}

          <div className="ci-lab">Template {emailChannel(type) || type === 'sms' ? <span style={{ color: 'var(--ci-txt3)' }}>· optional</span> : null}</div>
          {type === 'note' ? (
            <div style={{ fontSize: 12, color: 'var(--ci-txt3)' }}>Internal notes are saved as drafts, not sent.</div>
          ) : (
            <>
              <select className="ci-inp" value={templateId} onChange={(e) => { setTemplateId(e.target.value); setSendResult(null); }}>
                <option value="">No template — send my draft</option>
                {channelTemplates.map((t) => (
                  <option key={String(t.id || t._id)} value={String(t.id || t._id)}>{t.name || 'Untitled template'}</option>
                ))}
              </select>
              {isConfirmationTemplate(selectedTemplate) && (
                <div
                  data-testid="ci-confirmation-tag"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, marginTop: 8,
                    padding: '7px 10px', borderRadius: 8,
                    background: LINK_KINDS.confirmation.colorSoft,
                    border: `1px solid ${LINK_KINDS.confirmation.colorBorder}`,
                  }}
                >
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: LINK_KINDS.confirmation.color, flex: 'none' }} />
                  <span style={{ fontSize: 12, color: LINK_KINDS.confirmation.color, fontWeight: 600 }}>Confirmation</span>
                  <span style={{ fontSize: 11.5, color: 'var(--ci-txt2)' }}>
                    Sends the celebration confirmation link — not the invitation link.
                  </span>
                </div>
              )}
              {(selectedTemplate?.media_urls || []).length > 0 && (
                <div
                  data-testid="ci-attachment-tag"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, marginTop: 6,
                    padding: '6px 10px', borderRadius: 8,
                    background: 'rgba(212,175,55,0.10)', border: '1px solid rgba(212,175,55,0.35)',
                  }}
                >
                  <Paperclip size={12} style={{ color: 'var(--ci-gold-hi)', flex: 'none' }} />
                  <span style={{ fontSize: 11.5, color: 'var(--ci-txt2)' }}>
                    Includes {(selectedTemplate.media_urls || []).length > 1
                      ? `${selectedTemplate.media_urls.length} image attachments`
                      : 'an image attachment'} — shown in the preview, personalized per guest.
                  </span>
                </div>
              )}
              <div style={{ fontSize: 11, color: 'var(--ci-txt3)', marginTop: 6 }}>
                {selectedTemplate
                  ? 'Subject & body below override the template content for this send.'
                  : 'Write freely below — on send, Milo saves your draft as a template behind the scenes so it still passes the safety gate.'}
              </div>
            </>
          )}

          {emailChannel(type) && (
            <>
              <div className="ci-lab">Subject</div>
              <input className="ci-inp" value={subject} onChange={(e) => { setSubject(e.target.value); setSendResult(null); }} />
            </>
          )}

          {/* Safety mode chip */}
          <div className="ci-lab">Safety</div>
          <div style={{ fontSize: 12, color: 'var(--ci-txt2)' }}>
            {settingsErr ? (
              <span style={{ color: 'var(--ci-gold-hi)' }}>{settingsErr}</span>
            ) : settings ? (
              <span><ShieldCheck size={12} style={{ verticalAlign: -2, marginRight: 4, color: 'var(--ci-green)' }} />Mode: <b style={{ color: 'var(--ci-txt)' }}>{modeLabel}</b></span>
            ) : 'Loading send settings…'}
          </div>
        </div>

        {/* CENTER — compose + live preview SIDE BY SIDE (fills the page width + height, no dead space below) */}
        <div className="ci-create-main">
          <div className="ci-glass ci-pane" style={{ overflow: 'hidden' }}>
            <div className="ci-pane-cap">Compose</div>
            <div className="ci-pane-body">
            {isHtmlChannel ? (
              // Real rich-text editor — draft freely, body becomes HTML.
              <RichEditor
                value={htmlBody}
                onChange={(html) => { setHtmlBody(html); setSendResult(null); }}
                placeholder="Write your message… use the toolbar to format, or insert merge tags."
                minHeight={200}
                headerFooter
                toolbarExtras={mergeExtras}
              />
            ) : (
              // SMS / note: plain text (HTML isn't deliverable here). A slim bar
              // keeps merge-tag + RSVP insertion available.
              <div className="ci-rte">
                <div className="ci-rte-bar">
                  <div style={{ position: 'relative' }}>
                    <button className="ci-rb" style={{ color: 'var(--ci-gold-hi)' }} onClick={(e) => { e.stopPropagation(); setWave(wave === 'merge' ? '' : 'merge'); }}>+ Merge Tags</button>
                    {wave === 'merge' && (
                      <div className="ci-menu" style={{ right: 'auto', left: 0, top: 34 }} onClick={(e) => e.stopPropagation()}>
                        {MERGE_TAGS.map((m) => (
                          <button key={m.token} onClick={() => { insertToken(m.token); setWave(''); }}>{m.label} <span style={{ color: 'var(--ci-txt3)' }}>{m.token}</span></button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button className="ci-rb" style={{ color: 'var(--ci-gold-hi)' }} onClick={() => insertToken(RSVP_TOKEN)}>RSVP link</button>
                </div>
                <div style={{ padding: 12 }}>
                  <textarea
                    ref={bodyRef}
                    className="ci-inp"
                    value={body}
                    onChange={(e) => { setBody(e.target.value); setSendResult(null); }}
                  />
                </div>
              </div>
            )}
            </div>
          </div>
          <div className="ci-glass ci-pane" style={{ overflow: 'hidden' }}>
            <div className="ci-pane-cap">Live preview</div>
            <div className="ci-preview-stage ci-pane-body" style={{ background: 'none' }}>
              {type !== 'note' && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: 'var(--ci-txt3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    {audienceGuests.length > 1 && (
                      <button onClick={prevPreview} title="Previous recipient" aria-label="Previous recipient"
                        style={{ flex: 'none', width: 22, height: 22, borderRadius: 6, border: '1px solid var(--ci-bd)', background: 'transparent', color: 'var(--ci-txt2)', cursor: 'pointer', display: 'grid', placeItems: 'center', lineHeight: 1 }}>‹</button>
                    )}
                    <span>
                      Previewing as <b style={{ color: 'var(--ci-txt2)' }}>{guestDisplayName(previewGuest)}</b>
                      {audienceGuests.length > 1 ? <> · <b style={{ color: 'var(--ci-txt2)' }}>{previewPos + 1}</b> of {audienceGuests.length}</> : null}
                    </span>
                    {audienceGuests.length > 1 && (
                      <button onClick={nextPreview} title="Next recipient" aria-label="Next recipient"
                        style={{ flex: 'none', width: 22, height: 22, borderRadius: 6, border: '1px solid var(--ci-bd)', background: 'transparent', color: 'var(--ci-txt2)', cursor: 'pointer', display: 'grid', placeItems: 'center', lineHeight: 1 }}>›</button>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--ci-txt3)', textAlign: 'center', marginTop: 2 }}>
                    {audienceGuests.length > 1 ? 'Step through recipients to spot-check each name & RSVP link.' : 'Each guest sees their own name and their household’s RSVP link.'}
                  </div>
                </div>
              )}
              {previewTokenMissing && (
                <div style={{ fontSize: 11, color: 'var(--ci-clay, #c87d8e)', marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 5, justifyContent: 'center' }}>
                  <AlertCircle size={12} style={{ flex: 'none', marginTop: 1 }} />
                  <span><b>{guestDisplayName(previewGuest)}</b> has no RSVP token — the link will be blank. Add one in Guests; no link is faked here.</span>
                </div>
              )}
              {type === 'email' && (
                <>
                  <p className="ci-pv-cap">Preview — <b>Email</b></p>
                  <div className="ci-email-doc">
                    <div className="ci-email-hd"><div className="ci-email-subj">{subject || '(no subject)'}</div><div className="ci-email-meta"><div className="ci-ea">M</div><div><div className="ci-from">Event Host</div><div className="ci-to">to {audienceLabel}</div></div></div></div>
                    <div className="ci-email-body">
                      {htmlBodyText
                        ? <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(openLinksInNewTab(resolveMergeTagsForPreview(htmlBody, previewGuest, previewEventName))) }} />
                        : <p style={{ color: '#9a9aa2' }}>Start typing your message…</p>}
                    </div>
                    {/* No forced footer — the host's composed body is the message. */}
                  </div>
                </>
              )}
              {type === 'sms' && (
                <>
                  <p className="ci-pv-cap">
                    Preview — <b>{previewMediaUrls.length || previewMediaMissing ? 'MMS' : 'SMS'}</b>
                    {(previewMediaUrls.length > 0 || previewMediaMissing) && (
                      <span style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--ci-gold-hi)', fontWeight: 700 }}>
                        <Paperclip size={11} />
                        {previewMediaUrls.length > 0
                          ? `${previewMediaUrls.length} attachment${previewMediaUrls.length > 1 ? 's' : ''}`
                          : 'attachment unavailable'}
                      </span>
                    )}
                  </p>
                  {/* Fill the LIVE PREVIEW column — the fixed 280px phone left the
                      panel mostly empty and made the attachment squint-sized. */}
                  <div className="ci-phone" style={{ width: '100%', maxWidth: 440 }}>
                    <div className="ci-phone-notch" />
                    <div className="ci-phone-hd"><div className="ci-pn">Event Host</div><div className="ci-ps">Text Message</div></div>
                    <div className="ci-phone-body" style={{ minHeight: 160 }}>
                      <div className="ci-sms ci-in" style={previewMediaUrls.length ? { maxWidth: '96%' } : undefined}>
                        {previewMediaUrls.map((u, i) => (
                          <img key={i} src={u} alt="MMS attachment preview"
                            style={{ display: 'block', width: '100%', borderRadius: 10, marginBottom: 6 }} />
                        ))}
                        {previewMediaMissing && (
                          <div style={{ fontSize: 10, fontStyle: 'italic', opacity: 0.75, marginBottom: 6 }}>
                            [attachment unavailable — this guest has no celebration link]
                          </div>
                        )}
                        {smsResolved ? linkifyText(smsResolved) : 'Start typing your message…'}
                      </div>
                    </div>
                  </div>
                  <div className="ci-sms-meta" style={{ maxWidth: 280 }}>
                    <span><b>{smsEstimate.segments}</b> segment{smsEstimate.segments > 1 ? 's' : ''} / recipient{audienceCount > 0 ? ` · ~${smsEstimate.segments * audienceCount} total` : ''}</span>
                    <span><b>{smsEstimate.chars}</b> / {smsEstimate.perSegment} chars{smsEstimate.unicode ? ' · Unicode' : ''}</span>
                  </div>
                  {smsCulprits.length > 0 && (
                    <div style={{ maxWidth: 280, marginTop: 6, fontSize: 11, color: 'var(--ci-clay, #c87d8e)', display: 'flex', alignItems: 'flex-start', gap: 5 }}>
                      <AlertCircle size={12} style={{ flex: 'none', marginTop: 1 }} />
                      <span>
                        Special characters drop you to <b>70 chars/segment</b> (from 160): {smsCulprits.join(', ')}. Removing them roughly halves the segments — and the cost.
                      </span>
                    </div>
                  )}
                  <div style={{ maxWidth: 280, marginTop: 4, fontSize: 10.5, color: 'var(--ci-txt3)' }}>
                    Estimate reflects the sent message (name + full RSVP link). Each guest receives one text; segments are how the carrier bills it.
                  </div>
                </>
              )}
              {type === 'invitation' && (
                <>
                  <p className="ci-pv-cap">Preview — <b>Invitation card</b></p>
                  <div className="ci-invite-prev">
                    <div className="ci-m">M</div>
                    <div className="ci-t">{subject || "You're Invited!"}</div>
                    {htmlBodyText
                      ? <div className="ci-b" dangerouslySetInnerHTML={{ __html: sanitizeHtml(openLinksInNewTab(resolveMergeTagsForPreview(htmlBody, previewGuest, previewEventName))) }} />
                      : <div className="ci-b">Your invitation message appears here.</div>}
                    {/* No forced RSVP — the host adds one via the "RSVP button" if they want it. */}
                  </div>
                </>
              )}
              {type === 'note' && (
                <>
                  <p className="ci-pv-cap">Preview — <b>Internal / website note</b></p>
                  <div className="ci-note-card" style={{ width: '100%', margin: '0 auto' }}>
                    <span className="ci-note-src"><FileText size={12} /> Internal Note</span>
                    <div className="ci-note-q">{(() => { const r = resolveMergeTagsForPreview(body, previewGuest, previewEventName); return r ? linkifyText(r) : 'Your note appears here.'; })()}</div>
                    <div className="ci-note-meta"><div className="ci-nm">Visible to<b>Event team only</b></div><div className="ci-nm">Audience<b>{audienceLabel}</b></div></div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT — Milo assistant */}
        <div className="ci-glass" style={{ padding: 16 }}>
          <div className="ci-ins-h" style={{ marginBottom: 10 }}><Sparkles size={15} /> Milo Assistant</div>

          {/* Tell Milo what you want — free-form draft → subject + body */}
          <div className="ci-lab" style={{ marginTop: 0 }}>Tell Milo what you want</div>
          <textarea
            className="ci-inp"
            style={{ minHeight: 70 }}
            value={miloPrompt}
            onChange={(e) => { setMiloPrompt(e.target.value); setMiloDraftNote(''); }}
            placeholder={type === 'sms'
              ? 'e.g. A short text reminding guests to RSVP by Friday'
              : 'e.g. A warm reminder to guests who haven’t RSVP’d, mention the deadline is Friday'}
          />
          <button
            className="ci-btn ci-gold ci-sm"
            style={{ width: '100%', marginTop: 8, justifyContent: 'center' }}
            disabled={miloDraftBusy}
            onClick={draftWithMilo}
          >
            {miloDraftBusy ? <><span className="ci-spinner" /> Milo is drafting…</> : <><Wand2 size={13} /> Draft this with Milo</>}
          </button>
          {miloDraftNote && <div style={{ fontSize: 11, color: 'var(--ci-txt2)', marginTop: 6 }}>{miloDraftNote}</div>}

          {(() => {
            const strength = evaluateStrength({ text: effectiveBodyText, subject, type });
            return (
              <>
                <div className="ci-strength" style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 11, color: 'var(--ci-txt3)' }}>Message Strength</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: strength.color }}>{strength.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--ci-txt2)', marginTop: 2 }}>
                    {emailChannel(type) || type === 'sms'
                      ? (sendBlockedReason ? sendBlockedReason : 'Ready to review & send.')
                      : 'Notes are internal — save as a draft.'}
                  </div>
                </div>
                <div className="ci-lab">Why this works</div>
                {strength.checks.map((c, i) => (
                  <div key={i} className="ci-chk" style={c.ok ? undefined : { opacity: 0.55 }}>
                    <span className="ci-c" style={c.ok ? undefined : { color: 'var(--ci-txt3)' }}>{c.ok ? '✓' : '○'}</span> {c.text}
                  </div>
                ))}
              </>
            );
          })()}

          <div className="ci-lab">Enhance with Milo</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {['Warmer', 'Formal', 'Luxury', 'Concise'].map((tone) => (
              <button key={tone} className="ci-btn ci-sm" disabled={miloBusy} onClick={() => enhanceWithMilo(tone.toLowerCase())}>
                <Sparkles size={12} /> {tone}
              </button>
            ))}
          </div>
          {miloBusy && <div className="ci-loading" style={{ paddingTop: 10 }}><span className="ci-spinner" /> Milo is rewriting…</div>}

          {miloNeedsKey && (
            <div className="ci-wave" role="note" style={{ marginTop: 12 }}>
              <div className="ci-wic"><Sparkles size={18} /></div>
              <div>
                <div className="ci-wt">Add your API key to enable Milo</div>
                <div className="ci-wp">Milo's rewrite needs a language-model key (EMERGENT_LLM_KEY). Add it in the server environment and this button will rewrite your draft on the spot.</div>
                <span className="ci-wtag">Action needed</span>
              </div>
            </div>
          )}

          {miloMsg && (
            <div className="ci-ins-suggest" style={{ marginTop: 12 }}>
              <div className="ci-h"><Sparkles size={14} /> Milo's {miloMsg.tone} rewrite</div>
              <p style={{ whiteSpace: 'pre-wrap' }}>{miloMsg.text}</p>
              {miloMsg.commentary && (
                <p style={{ whiteSpace: 'pre-wrap', fontSize: 11, color: 'var(--ci-txt2)', marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(160,160,170,0.18)' }}>
                  <b>Milo's note</b> (not part of the message): {miloMsg.commentary}
                </p>
              )}
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="ci-btn ci-gold ci-sm" style={{ flex: 1 }} onClick={applyMilo}>Use this</button>
                <button className="ci-btn ci-sm" onClick={() => setMiloMsg(null)}>Dismiss</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---- Cc / additional recipients modal (host 2026-07-16) ---- */}
      {ccOpen && (
        <div style={BACKDROP_STYLE} role="dialog" aria-modal="true" aria-label="Add extra recipients" onClick={() => setCcOpen(false)}>
          <div className="ci-glass" style={{ width: 'min(520px, 94vw)', maxHeight: '78vh', display: 'flex', flexDirection: 'column', padding: 18 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div className="ci-ins-h" style={{ margin: 0 }}><Users size={15} /> Cc / add recipients</div>
              <button className="ci-kebab" aria-label="Close" onClick={() => setCcOpen(false)}><X size={16} /></button>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--ci-txt3)', marginBottom: 10 }}>
              Added on top of “{audienceLabel.replace(/ \+ \d+ added$/, '')}”. Each person receives their own personalized {type === 'sms' ? 'text' : 'email'} — addresses are never exposed to other guests.
            </div>
            <div style={{ position: 'relative', marginBottom: 8 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--ci-txt3)' }} />
              <input className="ci-inp" style={{ paddingLeft: 30 }} value={ccSearch} onChange={(e) => setCcSearch(e.target.value)}
                     placeholder={`Search guests by name${type === 'sms' ? ', phone' : ', email'}…`} />
            </div>
            <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {(() => {
                const q = ccSearch.trim().toLowerCase();
                const inAudience = new Set(
                  (selectedIds === null ? segmentGuests : audienceGuests.filter((g) => !extraSet.has(guestId(g)))).map(guestId));
                const rows = eligibleGuests.filter((g) => {
                  if (!q) return true;
                  return guestDisplayName(g).toLowerCase().includes(q)
                    || String(g.email || '').toLowerCase().includes(q)
                    || String(g.phone || '').toLowerCase().includes(q);
                });
                if (!rows.length) return <div className="ci-empty">No guests match this search.</div>;
                return rows.map((g) => {
                  const id = guestId(g);
                  const already = inAudience.has(id);
                  const on = extraSet.has(id);
                  return (
                    <label key={id} className={`ci-conv ${on ? 'ci-on' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', cursor: already ? 'default' : 'pointer', opacity: already ? 0.45 : 1 }}>
                      <input type="checkbox" checked={on || already} disabled={already} onChange={() => !already && toggleExtra(id)} />
                      <span className="ci-ea" style={{ width: 28, height: 28, fontSize: 11 }}>{initials(guestDisplayName(g))}</span>
                      <span style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: 'var(--ci-txt)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{guestDisplayName(g)}</div>
                        <div style={{ fontSize: 11, color: 'var(--ci-txt3)' }}>{already ? 'Already in the audience' : (type === 'sms' ? (g.phone || '') : (g.email || ''))}</div>
                      </span>
                    </label>
                  );
                });
              })()}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              {extraIds.length > 0 && <button className="ci-btn ci-sm" onClick={() => setExtraIds([])}>Remove all added</button>}
              <button className="ci-btn ci-gold ci-sm" onClick={() => setCcOpen(false)}>Done{extraIds.length ? ` · ${extraIds.length} added` : ''}</button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Customize Audience modal ---- */}
      {audienceOpen && (
        <div style={BACKDROP_STYLE} role="dialog" aria-modal="true" aria-label="Customize audience" onClick={() => setAudienceOpen(false)}>
          <div className="ci-glass" style={{ width: 'min(560px, 94vw)', maxHeight: '82vh', display: 'flex', flexDirection: 'column', padding: 18 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div className="ci-ins-h" style={{ margin: 0 }}><Users size={15} /> Customize Audience</div>
              <button className="ci-kebab" aria-label="Close" onClick={() => setAudienceOpen(false)}><X size={16} /></button>
            </div>
            {/* #8: chips are a VIEW FILTER over the eligible list — they no longer
                overwrite the manual selection (which previously discarded every
                hand-picked recipient on each filter click). Switching filters now
                only changes what's SHOWN; selections persist. Only Clear all /
                Reset to segment / Cancel discard them. */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--ci-txt3)' }}>Filter view:</span>
              {SEGMENTS.map((s) => (
                <button
                  key={s.key}
                  className={`ci-fchip ${audienceFilter === s.key ? 'ci-on' : ''}`}
                  onClick={() => setAudienceFilter(s.key)}
                >{s.label}</button>
              ))}
            </div>
            <div style={{ position: 'relative', marginBottom: 8 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--ci-txt3)' }} />
              <input
                className="ci-inp"
                style={{ paddingLeft: 30 }}
                value={recipientSearch}
                onChange={(e) => setRecipientSearch(e.target.value)}
                placeholder={`Search guests by name${type === 'sms' ? ', phone' : ', email'}…`}
              />
            </div>
            <div style={{ fontSize: 12, color: 'var(--ci-txt2)', marginBottom: 8 }}>
              <b style={{ color: 'var(--ci-txt)' }}>{audienceCount}</b> selected · {filteredEligible.length} shown
            </div>
            <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {filteredEligible.map((g) => {
                const id = guestId(g);
                const on = (selectedIds || []).includes(id);
                const reachable = g._reachable !== false;
                return (
                  <label key={id} className={`ci-conv ${on ? 'ci-on' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', cursor: reachable ? 'pointer' : 'not-allowed', opacity: reachable ? 1 : 0.5, borderLeft: '3px solid transparent' }}>
                    <input type="checkbox" checked={on} disabled={!reachable} onChange={() => reachable && toggleRecipient(id)} />
                    <span className="ci-ea" style={{ width: 28, height: 28, fontSize: 11 }}>{initials(guestDisplayName(g))}</span>
                    <span style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: 'var(--ci-txt)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{guestDisplayName(g)}</div>
                      <div style={{ fontSize: 11, color: reachable ? 'var(--ci-txt3)' : '#c87d8e' }}>{reachable ? (type === 'sms' ? (g.phone || 'no phone') : (g.email || 'no email')) : `Can't ${type === 'sms' ? 'text' : 'email'} — ${g._why}`}</div>
                    </span>
                    {on && <Plus size={14} style={{ marginLeft: 'auto', transform: 'rotate(45deg)', color: 'var(--ci-gold-hi)' }} />}
                  </label>
                );
              })}
              {!filteredEligible.length && (
                <div className="ci-empty">No guests match this search.</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              {/* Clear must commit an EXPLICIT empty set ([]), NOT reset to the
                  segment (null). Resetting to null silently re-targets the whole
                  segment — a mass-send risk. Use "Reset to segment" / a segment
                  chip above to follow a segment again. */}
              <button className="ci-btn ci-sm" onClick={() => { setSelectedIds([]); setRecipientSearch(''); setSendResult(null); }}>Clear all</button>
              <button className="ci-btn ci-gold ci-sm" onClick={() => setAudienceOpen(false)}>Done · {audienceCount} selected</button>
            </div>
          </div>
        </div>
      )}

      {/* ---- #7: View all recipients (READ-ONLY roster) ----
           A read-only mirror of audienceGuests — distinct from Customize Audience
           (no toggles, nothing changes the send). Name + email/phone per row, a
           search box, and "Copy all" (the full roster to the clipboard). Reuses
           BACKDROP_STYLE + ci-glass, mirroring the modal pattern above. */}
      {viewAllOpen && (() => {
        const channelContact = (g) => (type === 'sms' ? String(g.phone || '').trim() : String(g.email || '').trim());
        const q = viewAllSearch.trim().toLowerCase();
        const rows = audienceGuests.filter((g) => {
          if (!q) return true;
          return guestDisplayName(g).toLowerCase().includes(q)
            || String(g.email || '').toLowerCase().includes(q)
            || String(g.phone || '').toLowerCase().includes(q);
        });
        const rosterText = audienceGuests
          .map((g) => { const c = channelContact(g); return c ? `${guestDisplayName(g)} <${c}>` : guestDisplayName(g); })
          .join('\n');
        const copyAll = () => {
          try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
              navigator.clipboard.writeText(rosterText)
                .then(() => { setRosterCopied(true); setTimeout(() => setRosterCopied(false), 2000); })
                .catch(() => { setRosterCopied(false); });
            }
          } catch { /* clipboard unavailable — no-op */ }
        };
        return (
          <div style={BACKDROP_STYLE} role="dialog" aria-modal="true" aria-label="View all recipients" onClick={() => setViewAllOpen(false)}>
            <div className="ci-glass" style={{ width: 'min(520px, 94vw)', maxHeight: '82vh', display: 'flex', flexDirection: 'column', padding: 18 }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div className="ci-ins-h" style={{ margin: 0 }}><Users size={15} /> All recipients ({audienceCount})</div>
                <button className="ci-kebab" aria-label="Close" onClick={() => setViewAllOpen(false)}><X size={16} /></button>
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--ci-txt3)', marginBottom: 8 }}>
                Read-only — this is exactly who will receive this {type === 'sms' ? 'text' : 'message'}. To add or remove anyone, use Customize Audience.
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--ci-txt3)' }} />
                  <input
                    className="ci-inp"
                    style={{ paddingLeft: 30 }}
                    value={viewAllSearch}
                    onChange={(e) => setViewAllSearch(e.target.value)}
                    placeholder={`Search recipients by name${type === 'sms' ? ', phone' : ', email'}…`}
                  />
                </div>
                <button className="ci-btn ci-sm" onClick={copyAll} title="Copy the full recipient roster to the clipboard">
                  {rosterCopied ? 'Copied ✓' : 'Copy all'}
                </button>
              </div>
              <div style={{ fontSize: 12, color: 'var(--ci-txt2)', marginBottom: 8 }}>
                {rows.length} of {audienceCount} shown
              </div>
              <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {rows.map((g) => {
                  const contact = channelContact(g);
                  return (
                    <div key={guestId(g)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px' }}>
                      <span className="ci-ea" style={{ width: 28, height: 28, fontSize: 11, flex: 'none' }}>{initials(guestDisplayName(g))}</span>
                      <span style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: 'var(--ci-txt)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{guestDisplayName(g)}</div>
                        <div style={{ fontSize: 11, color: 'var(--ci-txt3)' }}>{contact || (type === 'sms' ? 'no phone' : 'no email')}</div>
                      </span>
                    </div>
                  );
                })}
                {!rows.length && <div className="ci-empty">No recipients match this search.</div>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                <button className="ci-btn ci-gold ci-sm" onClick={() => setViewAllOpen(false)}>Done</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ---- Review & Schedule confirmation modal ---- */}
      {confirmOpen && (
        <div style={BACKDROP_STYLE} role="dialog" aria-modal="true" aria-label="Confirm send" onClick={() => !sending && setConfirmOpen(false)}>
          <div className="ci-glass" style={{ width: 'min(460px, 94vw)', padding: 22 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <ShieldCheck size={20} style={{ color: sendMode === 'live' ? 'var(--ci-gold-hi)' : 'var(--ci-green)' }} />
              <div className="ci-ins-h" style={{ margin: 0 }}>Everything look right?</div>
            </div>
            <p style={{ fontSize: 13, color: 'var(--ci-txt2)', lineHeight: 1.5, marginTop: 0 }}>
              {sendMode === 'live'
                ? `This will send to ${audienceCount} real recipient${audienceCount === 1 ? '' : 's'}.`
                : sendMode === 'test'
                  ? `Test Mode is on — this records a test send for ${audienceCount} recipient${audienceCount === 1 ? '' : 's'} without contacting real guests.`
                  : `Outbound is on Hold — this queues ${audienceCount} message${audienceCount === 1 ? '' : 's'} in the Outbox for your review before anything goes out.`}
            </p>
            <div className="ci-strength" style={{ margin: '12px 0' }}>
              <div className="ci-chk" style={{ justifyContent: 'space-between' }}><span>Channel</span><b style={{ color: 'var(--ci-txt)' }}>{TYPES.find((t) => t.key === type)?.label}</b></div>
              <div className="ci-chk" style={{ justifyContent: 'space-between' }}><span>Template</span><b style={{ color: 'var(--ci-txt)' }}>{selectedTemplate?.name || 'Auto-created from your draft'}</b></div>
              <div className="ci-chk" style={{ justifyContent: 'space-between' }}><span>Recipients</span><b style={{ color: 'var(--ci-txt)' }}>{audienceCount} · {audienceLabel}</b></div>
              {type === 'sms' && (
                <div className="ci-chk" style={{ justifyContent: 'space-between' }}>
                  <span>Est. SMS segments</span>
                  <b style={{ color: smsEstimate.unicode ? 'var(--ci-clay, #c87d8e)' : 'var(--ci-txt)' }}>
                    {smsEstimate.segments}/recipient · ~{smsEstimate.segments * audienceCount} total{smsEstimate.unicode ? ' · Unicode' : ''}
                  </b>
                </div>
              )}
              {emailChannel(type) && <div className="ci-chk" style={{ justifyContent: 'space-between' }}><span>Subject</span><b style={{ color: 'var(--ci-txt)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subject}</b></div>}
              {usesRsvpLink && (
                <div className="ci-chk" style={{ justifyContent: 'space-between', gap: 10 }}>
                  <span>RSVP link</span>
                  {previewRsvpLink
                    ? <a href={previewRsvpLink} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ci-gold-hi)', textDecoration: 'underline', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`Open ${guestDisplayName(previewGuest)}'s RSVP page`}>Test link ({guestDisplayName(previewGuest)}) ↗</a>
                    : <b style={{ color: 'var(--ci-clay, #c87d8e)' }}>No token for {guestDisplayName(previewGuest)}</b>}
                </div>
              )}
              <div className="ci-chk" style={{ justifyContent: 'space-between', marginBottom: 0 }}><span>Safety mode</span><b style={{ color: sendMode === 'live' ? 'var(--ci-gold-hi)' : 'var(--ci-green)' }}>{modeLabel}</b></div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="ci-btn ci-sm" onClick={() => setConfirmOpen(false)} disabled={sending}>Cancel</button>
              <button className="ci-btn ci-gold ci-sm" onClick={handleConfirmSend} disabled={sending}>
                {sending ? 'Working…' : `${sendVerb} to ${audienceCount}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save as Template modal (#6) — name the template before saving */}
      {tplModal && (
        <div style={BACKDROP_STYLE} role="dialog" aria-modal="true" aria-label="Save as template" onClick={() => !savingTpl && setTplModal(null)}>
          <div className="ci-glass" style={{ width: 'min(440px, 94vw)', padding: 22 }} onClick={(e) => e.stopPropagation()}>
            <div className="ci-ins-h" style={{ margin: '0 0 4px' }}><Save size={16} style={{ verticalAlign: -3, marginRight: 6 }} />Save as template</div>
            <p style={{ fontSize: 12.5, color: 'var(--ci-txt2)', marginTop: 0 }}>Name this {type === 'sms' ? 'SMS' : type === 'note' ? 'note' : 'email'} template so you can find and reuse it later.</p>
            <label style={{ fontSize: 11, color: 'var(--ci-txt3)', display: 'block', marginBottom: 4 }}>Template name</label>
            <input
              className="ci-inp" autoFocus value={tplModal.name}
              onChange={(e) => setTplModal((m) => ({ ...m, name: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter' && tplModal.name.trim() && !savingTpl) handleSaveAsTemplate(tplModal.name); }}
              placeholder="e.g. RSVP reminder — SMS"
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              <button className="ci-btn ci-sm" onClick={() => setTplModal(null)} disabled={savingTpl}>Cancel</button>
              <button className="ci-btn ci-gold ci-sm" disabled={savingTpl || !tplModal.name.trim()} onClick={() => handleSaveAsTemplate(tplModal.name)}>
                {savingTpl ? 'Saving…' : 'Save template'}
              </button>
            </div>
            {tplSaveResult && <div style={{ fontSize: 12, color: 'var(--ci-txt2)', marginTop: 8 }}>{tplSaveResult}</div>}
          </div>
        </div>
      )}

      {/* Blocked surface — keeps the Review button from being a dead end */}
      {wave === 'blocked' && (
        <ComingWave title="Resolve this before sending" tag="Action needed">
          {sendBlockedReason || 'Complete the required fields to send.'}
        </ComingWave>
      )}
    </div>
  );
}

// Campaign-aware "Pending RSVP" eligibility summary. Presents a single,
// SELF-RECONCILING statement the host can add up:
//   "{eligible} eligible + {excluded} excluded = {base} on your list"
// where `base` is stated explicitly and EXCLUDES deleted guests (so they never
// inflate the total). Deleted guests, when present, are shown as a separate
// "Deleted guests" aside — relabelled from the backend's raw reason and never
// folded into the base or the excluded breakdown. When a custom selection
// narrows the send, a second line states "Sending to N of {eligible}" so the
// recipient count above never reads as contradicting the eligibility math.
// Self-contained styling (design tokens only). Renders a graceful loading
// state, and a quiet fallback note when the live audience is unavailable
// (the preset still works off the client-side filter).
function PendingAudienceSummary({ loading, error, audience, sendCount, customized, expanded, onToggle }) {
  if (loading && !audience) {
    return (
      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ci-txt3)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span className="ci-spinner" /> Checking who still needs to RSVP…
      </div>
    );
  }
  // No live audience (error or not yet loaded): show a quiet fallback note so
  // the host knows the count is a local estimate — never a dead end.
  if (!audience) {
    if (!error) return null;
    return (
      <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--ci-txt3)', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <Info size={12} style={{ flex: 'none', marginTop: 1 }} />
        <span>{error}</span>
      </div>
    );
  }

  // All counts come from ONE normalized source so they reconcile by construction:
  //   eligibleCount + reportedExcluded === baseCount   (deleted held out).
  const eligibleCount = audience.eligibleCount || 0;
  const excluded = audience.reportedExcluded || 0; // excludes deleted guests
  const base = audience.baseCount || (eligibleCount + excluded);
  const deletedCount = audience.deletedCount || 0;
  const reasons = audience.reasons || []; // already deleted-free
  const topReason = reasons[0]?.label;
  const hasDetail = reasons.length > 0;
  // The send audience differs from "eligible" only when a custom selection
  // narrows it; surface that explicitly instead of letting the two counts clash.
  const send = Number.isFinite(sendCount) ? sendCount : eligibleCount;
  const narrowed = customized && send !== eligibleCount;

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 12, color: 'var(--ci-txt2)' }}>
        <ShieldCheck size={12} style={{ verticalAlign: -2, color: 'var(--ci-green)', flex: 'none' }} />
        <span>
          <b style={{ color: 'var(--ci-txt)' }}>{eligibleCount}</b> eligible
          {excluded > 0 && (
            <>
              {' + '}
              <b style={{ color: 'var(--ci-txt)' }}>{excluded}</b> excluded
              {topReason ? ` (${topReason})` : ''}
            </>
          )}
          {' = '}
          <b style={{ color: 'var(--ci-txt)' }}>{base}</b> on your list
        </span>
        {hasDetail && (
          <button
            type="button"
            onClick={onToggle}
            title={expanded ? 'Hide why guests were excluded' : 'See why guests were excluded'}
            aria-expanded={expanded}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 2, marginLeft: 2,
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              color: 'var(--ci-gold-hi)', fontSize: 12,
            }}
          >
            {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            {expanded ? 'Hide' : 'Why'}
          </button>
        )}
      </div>
      {narrowed && (
        <div style={{ fontSize: 11.5, color: 'var(--ci-txt2)', marginTop: 3 }}>
          Sending to <b style={{ color: 'var(--ci-txt)' }}>{send}</b> of {eligibleCount} eligible (custom selection).
        </div>
      )}
      <div style={{ fontSize: 11, color: 'var(--ci-txt3)', marginTop: 3 }}>
        No double-contact — already-answered and already-contacted guests are skipped.
        {deletedCount > 0 && ` Deleted guests (${deletedCount}) aren’t counted in your list.`}
      </div>
      {expanded && hasDetail && (
        <div style={{
          marginTop: 8, padding: '8px 10px', borderRadius: 8,
          background: 'rgba(255,255,255,0.03)', border: '1px solid var(--ci-line, rgba(255,255,255,0.08))',
          display: 'flex', flexDirection: 'column', gap: 5,
        }}>
          <div style={{ fontSize: 10.5, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--ci-txt3)' }}>
            Excluded · {excluded}
          </div>
          {reasons.map((r) => (
            <div key={r.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 12, color: 'var(--ci-txt2)' }}>
              <span>{r.label}</span>
              <b style={{ color: 'var(--ci-txt)', flex: 'none' }}>{r.count}</b>
            </div>
          ))}
          {deletedCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 12, color: 'var(--ci-txt3)', marginTop: 2, paddingTop: 5, borderTop: '1px solid var(--ci-line, rgba(255,255,255,0.08))' }}>
              <span>{DELETED_REASON_DISPLAY} <span style={{ color: 'var(--ci-txt3)' }}>(not in your list)</span></span>
              <b style={{ color: 'var(--ci-txt2)', flex: 'none' }}>{deletedCount}</b>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Merge-tag + RSVP-link toolbar group for the rich editor. Self-contained
// (manages its own dropdown) and styled with the editor's re-* classes so it
// reads as part of the toolbar. onInsert(token) drops the token at the caret.
function MergeTagButtons({ onInsert, onInsertHtml }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);
  return (
    <>
      <div className="re-pop-host" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="re-tb re-wide"
          style={{ color: 'var(--ci-gold-hi)' }}
          title="Insert a merge tag"
          onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o); }}
        >
          + Merge tags
        </button>
        {open && (
          <div className="re-pop" onMouseDown={(e) => e.preventDefault()}>
            {MERGE_TAGS.map((m) => (
              <button key={m.token} type="button" className="re-pop-row"
                onMouseDown={(e) => { e.preventDefault(); onInsert(m.token); setOpen(false); }}>
                {m.label} <span style={{ color: 'var(--ci-txt3)' }}>{m.token}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        className="re-tb re-wide"
        style={{ color: 'var(--ci-gold-hi)' }}
        title="Insert the RSVP link as plain text"
        onMouseDown={(e) => { e.preventDefault(); onInsert(RSVP_TOKEN); }}
      >
        RSVP link
      </button>
      {onInsertHtml && (
        <button
          type="button"
          className="re-tb re-wide"
          style={{ color: 'var(--ci-gold-hi)' }}
          title="Insert a styled RSVP button — edit, move, or delete it freely"
          onMouseDown={(e) => { e.preventDefault(); onInsertHtml(RSVP_BUTTON_HTML); }}
        >
          RSVP button
        </button>
      )}
    </>
  );
}
